import type {
  Dispatch,
  KeyboardEvent,
  MutableRefObject,
  PointerEvent,
  ReactNode,
  SetStateAction,
} from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { isTruthy, isTruthyAndNotEmpty } from '@stimcar/libs-kernel';
import type { StudioCapabilityId } from '../host/auth/capabilities.js';
import { lastAssistantText, thinkingTrace } from '../host/agentProgress.js';
import {
  deployedAppHostname,
  deployedAppUrl,
  tryAssertDeployableScratchSlug,
} from '../host/deployments/hostname.js';
import { runBackground } from '../host/runBackground.js';
import {
  DEFAULT_HOST_SCRATCH_APP_SLUG,
  parseScratchAppSlugFromTargetId,
} from '../host/targets/scratchApp/constants.js';
import {
  hostPreviewPathOrApps,
  isHostPreviewPath,
  isKnownStudioPagePath,
  isLegacyHomePath,
  targetIdFromHostPreviewPath,
} from '../hostPreviewPaths.js';
import * as m from '../paraglide/messages.js';
import { matchStudioRoute, StudioBackendRoutes, StudioPageRoutes } from '../studioRoutes.js';
import type {
  AgentModelOptionView,
  AgentProgressKind,
  DeployedAppCommitSummaryView,
  DeployedAppSummaryView,
  DeploySessionRequest,
  FavoriteSessionSummaryView,
  IncomingAttachment,
  SessionSummaryView,
  SessionView,
  SessionVisibility,
  StudioPublicSession,
  StudioTargetSummaryView,
} from './api.js';
import type { DraftAttachmentFile, PendingChatAttachment } from './chatAttachments.js';
import type { PreviewPathHistoryState, PreviewPathPreset } from './hostPreview.js';
import type { PickerSectionId } from './pickerSection.js';
import type { PreviewDevice } from './previewDevice.js';
import type { StudioInspectSelection } from './previewInspect.js';
import type { WelcomeTitleKind } from './welcomeGreeting.js';
import {
  abandonSession,
  cancelSessionTurn,
  createScratchApp,
  createSession,
  deleteScratchApp,
  deploySession,
  discardTeachBrowser,
  fetchAgentStatus,
  fetchHostPreviewStatus,
  fetchLastIndependentSession,
  fetchSession,
  listDeploymentCommits,
  listDeployments,
  listFavorites,
  listSessions,
  listTargets,
  loadStudioSession,
  mergeSessionFavoriteFlags,
  openSessionMergeRequest,
  pinFavorite,
  postSessionMessage,
  promoteTeachBrowser,
  renameSessionTitle,
  revertDeployment,
  setSessionAgentModel,
  skipSourceConnect,
  startSessionPreview,
  startSourceConnect,
  startTeachBrowser,
  stopDeployment,
  stopTeachBrowser,
  StudioApiError,
  syncSessionBase,
  touchSessionPreview,
  unpinFavorite,
  updateSessionSharing,
} from './api.js';
import logoUrl from './assets/logo.png';
import { consumeAuthenticationMessage } from './auth.js';
import { AutomationsPage } from './AutomationsPage.js';
import {
  canonicalAttachmentMimeType,
  createDraftAttachment,
  pendingAttachmentFromDraft,
  revokeDraftAttachment,
  revokeDraftAttachments,
} from './chatAttachments.js';
import { ChatComposer } from './chatComposer.js';
import {
  CHAT_COLLAPSED_RAIL_WIDTH,
  toggleChatCollapsed,
  toggleChatMaximized,
} from './chatLayout.js';
import { ChatMarkdown } from './chatMarkdown.js';
import { ChatNotice } from './chatNotice.js';
import {
  DEFAULT_CHAT_WIDTH,
  MAX_CHAT_WIDTH,
  MIN_CHAT_WIDTH,
  nextChatWidthFromDrag,
  nextChatWidthFromKey,
} from './chatResize.js';
import { chatFollowScrollTop, isChatStuckToBottom } from './chatScroll.js';
import {
  composerHasContent,
  shouldCancelTurnOnEscape,
  stepUserMessageHistory,
  userMessageTexts,
} from './composerKeys.js';
import { ConversationRail } from './conversationRail.js';
import { scheduleFavoriteThumbnailCapture } from './favoriteThumbnailCapture.js';
import {
  createPreviewPathHistory,
  HOST_PREVIEW_STATUS_POLL_MS,
  normalizePreviewPath,
  parsePreviewPathInput,
  previewPathHistoryBack,
  previewPathHistoryCanBack,
  previewPathHistoryCanForward,
  previewPathHistoryCurrent,
  previewPathHistoryForward,
  previewPathPresetsForTarget,
  pushPreviewPathHistory,
  shouldFetchHostPreviewStatus,
  shouldReloadHostPreview,
  shouldReloadPreviewAfterAgentTurn,
  shouldReloadPreviewOnInspectToggle,
  withPreviewPath,
  withStudioReloadQuery,
} from './hostPreview.js';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CollapseChatIcon,
  DesktopIcon,
  ExpandChatIcon,
  GitMergeIcon,
  HistoryIcon,
  LinkIcon,
  MaximizeIcon,
  MobileIcon,
  RestoreIcon,
  SendIcon,
  StarIcon,
  SyncBaseIcon,
  TabletIcon,
  TrashIcon,
} from './icons.js';
import { resolveInitialPickerSection, writeStoredPickerSectionId } from './pickerSection.js';
import {
  PREVIEW_DEVICES,
  previewDeviceFrameClassName,
  previewDeviceFrameStyle,
} from './previewDevice.js';
import {
  isPreviewMessageOrigin,
  isStudioInspectReady,
  isStudioInspectSelection,
  isStudioLocationPush,
  lastInspectSelection,
  mergeComposerPrompt,
  postToPreviewFrame,
  requestPreviewLocation,
  STUDIO_INSPECT_READY_TYPE,
  STUDIO_INSPECT_SELECTION_TYPE,
  STUDIO_LOCATION_PUSH_TYPE,
  studioInspectRequest,
} from './previewInspect.js';
import { isPreviewTabActive, PREVIEW_HEARTBEAT_MS } from './previewPresence.js';
import {
  capturePreviewScreenshot,
  isPreviewScreenshotSupported,
  selectionScreenshotFileName,
} from './previewScreenshot.js';
import { PromptActivityPanel } from './PromptActivityPanel.js';
import { slugifyScratchAppName } from './scratchAppSlug.js';
import {
  readStoredSelectedTargetId,
  resolveSelectedTargetId,
  writeStoredSelectedTargetId,
} from './selectedTarget.js';
import { STUDIO_SESSION_KEEPALIVE_MS } from './sessionKeepalive.js';
import { sessionPollDelayMs } from './sessionPoll.js';
import { SessionSharingPanel } from './SessionSharingPanel.js';
import {
  readShowBrowserLivePreview,
  writeShowBrowserLivePreview,
} from './showBrowserLivePreview.js';
import { readShowCreateScratchAppMenu } from './showCreateScratchAppMenu.js';
import { readShowDeleteScratchAppMenu } from './showDeleteScratchAppMenu.js';
import { readShowPreviewPathBar } from './showPreviewPathBar.js';
import { readShowPromptActivityLog, writeShowPromptActivityLog } from './showPromptActivityLog.js';
import {
  appendDictationTranscript,
  isSpeechDictationSupported,
  startSpeechDictation,
  stopSpeechDictation,
} from './speechDictation.js';
import { StudioSettingsPanel } from './StudioSettingsPanel.js';
import { TargetTileOverflowMenu, TargetTileOverflowMenuItem } from './TargetTileOverflowMenu.js';
import {
  initialWelcomeTitleIndex,
  nextWelcomeTitleIndex,
  resolveWelcomeFirstName,
  WELCOME_TITLE_KINDS,
  WELCOME_TITLE_ROTATE_MS,
} from './welcomeGreeting.js';

function welcomeTitleFor(kind: WelcomeTitleKind, name: string): string {
  switch (kind) {
    case 'hello':
      return m.studioWelcomeTitleHello({ name });
    case 'embellish':
      return m.studioWelcomeTitleEmbellish({ name });
    case 'idea':
      return m.studioWelcomeTitleIdea({ name });
    case 'attack':
      return m.studioWelcomeTitleAttack({ name });
    case 'improve':
    default:
      return m.studioWelcomeTitleImprove({ name });
  }
}

export type AnonymousPageProps = {
  readonly message?: string;
};

export type AccountBarProps = {
  readonly email: string;
  readonly onUnauthorized: () => void;
  readonly onOpenTargetMenu?: () => void;
  readonly onShowCreateScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowDeleteScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowPreviewPathBarChange?: (enabled: boolean) => void;
  readonly onShowPromptActivityLogChange?: (enabled: boolean) => void;
  readonly previewDevice?: PreviewDevice;
  readonly onPreviewDeviceChange?: (device: PreviewDevice) => void;
};

export type AuthenticatedStudioProps = {
  readonly email: string;
  readonly givenName?: string;
  readonly canCreateScratchApp: boolean;
  readonly capabilities: readonly StudioCapabilityId[];
  readonly onUnauthorized: () => void;
};

export type TargetPickerPageProps = {
  readonly firstName: string;
  readonly targets: readonly StudioTargetSummaryView[];
  readonly favorites?: readonly FavoriteSessionSummaryView[];
  readonly deployments?: readonly DeployedAppSummaryView[];
  readonly lastIndependentSession?: SessionSummaryView | null;
  readonly error: string | undefined;
  readonly creatingScratchApp?: boolean;
  readonly canCreateScratchApp?: boolean;
  /** Test / override: force the initially active picker section. */
  readonly initialSection?: PickerSectionId;
  readonly quickStartDraft?: string;
  readonly quickStartBusy?: boolean;
  readonly hostBusySessionCount?: number;
  readonly onQuickStartDraftChange?: (value: string) => void;
  readonly onQuickStart?: () => void;
  readonly onSelectTarget: (targetId: string) => void;
  readonly onSelectFavorite?: (urlPath: string) => void;
  readonly onSelectLastIndependent?: (urlPath: string) => void;
  readonly onSelectDeployment?: (deployment: DeployedAppSummaryView) => void;
  readonly onEditDeployment?: (deployment: DeployedAppSummaryView) => void;
  readonly onStopDeployment?: (deployment: DeployedAppSummaryView) => void;
  readonly onRevertDeployment?: (deployment: DeployedAppSummaryView, sha: string) => void;
  readonly onCreateScratchApp: (slug: string) => void;
  readonly canDeleteScratchApp?: boolean;
  readonly deletingScratchAppSlug?: string | undefined;
  readonly onDeleteScratchApp?: (slug: string) => void;
};

/** Target id for the shared starter independent app, when present in the picker list. */
export function resolveQuickStartTargetId(
  targets: readonly StudioTargetSummaryView[]
): string | undefined {
  return targets.find(isDefaultIndependentAppTile)?.id;
}

export type PartitionedPickerTargets = {
  readonly website: readonly StudioTargetSummaryView[];
  readonly independent: readonly StudioTargetSummaryView[];
};

function isDefaultIndependentAppTile(target: StudioTargetSummaryView): boolean {
  return parseScratchAppSlugFromTargetId(target.id) === DEFAULT_HOST_SCRATCH_APP_SLUG;
}

function hostBusyBannerMessage(busySessionCount: number): string | undefined {
  if (busySessionCount <= 0) {
    return undefined;
  }
  if (busySessionCount === 1) {
    return m.agentHostBusyOne();
  }
  return m.agentHostBusyMany({ count: busySessionCount });
}

/** Independent tiles: « Application indépendante » (application-standard) always first. */
export function orderIndependentPickerTargets(
  targets: readonly StudioTargetSummaryView[]
): readonly StudioTargetSummaryView[] {
  const defaultTile = targets.filter(isDefaultIndependentAppTile);
  const others = targets.filter((target) => !isDefaultIndependentAppTile(target));
  return [...defaultTile, ...others];
}

export function partitionPickerTargets(
  targets: readonly StudioTargetSummaryView[]
): PartitionedPickerTargets {
  return {
    website: targets.filter((target) => target.kind === 'public-web'),
    independent: orderIndependentPickerTargets(
      targets.filter((target) => target.kind !== 'public-web')
    ),
  };
}

export type HomePageProps = {
  readonly requestDraft: string;
  readonly error: string | undefined;
  readonly startingRequest?: boolean;
  readonly pendingAttachments?: readonly PendingChatAttachment[];
  readonly selectedComponents?: readonly StudioInspectSelection[];
  readonly inspectEnabled?: boolean;
  readonly dictationSupported?: boolean;
  readonly dictationListening?: boolean;
  readonly chatMaximized?: boolean;
  readonly conversationRailOpen?: boolean;
  readonly onRequestDraftChange: (value: string) => void;
  readonly onStartRequest: () => void;
  readonly onPickAttachments?: (files: readonly File[]) => void;
  readonly onRemoveAttachment?: (id: string) => void;
  readonly onRemoveSelection?: (index: number) => void;
  readonly screenshotSupported?: boolean;
  readonly onCaptureScreenshot?: () => void;
  readonly screenshotUsesSelection?: boolean;
  readonly onToggleInspect?: () => void;
  readonly onToggleDictation?: () => void;
  readonly onToggleMaximize?: () => void;
  readonly onToggleConversationRail?: () => void;
  readonly agentModelId?: string;
  readonly agentModels?: readonly AgentModelOptionView[];
  readonly onAgentModelChange?: (modelId: string) => void;
};

export type SessionPageProps = {
  readonly session: SessionView;
  readonly draft: string;
  readonly recallingHistory: boolean;
  readonly pendingAttachments?: readonly PendingChatAttachment[];
  readonly selectedComponents?: readonly StudioInspectSelection[];
  readonly inspectEnabled?: boolean;
  readonly dictationSupported?: boolean;
  readonly dictationListening?: boolean;
  readonly chatMaximized?: boolean;
  readonly conversationRailOpen?: boolean;
  readonly notice: string | undefined;
  readonly onDraftChange: (value: string) => void;
  readonly onPickAttachments?: (files: readonly File[]) => void;
  readonly onRemoveAttachment?: (id: string) => void;
  readonly onRemoveSelection?: (index: number) => void;
  readonly screenshotSupported?: boolean;
  readonly onCaptureScreenshot?: () => void;
  readonly screenshotUsesSelection?: boolean;
  readonly onToggleInspect?: () => void;
  readonly onToggleDictation?: () => void;
  readonly onToggleMaximize?: () => void;
  readonly onToggleConversationRail?: () => void;
  readonly onRenameTitle?: (title: string) => void;
  readonly mergeRequestStatus?: 'opened' | 'updated';
  readonly onSend: () => void;
  readonly onStartPreview: () => void;
  readonly onOpenMergeRequest: () => void;
  readonly onSyncSessionBase: () => void;
  readonly canDeploy?: boolean;
  readonly isDeployed?: boolean;
  readonly deployBusy?: boolean;
  readonly currentScratchSlug?: string;
  readonly deployedSlugs?: readonly string[];
  readonly onDeploySession?: (request: DeploySessionRequest) => void;
  readonly onAbandon: () => void;
  readonly onCopyLink: () => void;
  readonly onToggleFavorite?: () => void;
  readonly onVisibilityChange?: (visibility: SessionVisibility) => void;
  readonly onSharedWithEmailsChange?: (emails: readonly string[]) => void;
  readonly sharingBusy?: boolean;
  readonly onStopTurn: () => void;
  readonly onRecallOlder: () => void;
  readonly onRecallNewer: () => void;
  readonly onAgentModelChange?: (modelId: string) => void;
  readonly onTeachStart?: () => void;
  readonly onTeachStop?: () => void;
  readonly onTeachPromote?: () => void;
  readonly onTeachDiscard?: () => void;
  readonly teachBusy?: boolean;
  readonly onSourceConnect?: (source: string) => void;
  readonly onSourceConnectSkip?: (source: string) => void;
  readonly sourceConnectBusy?: boolean;
};

function ConversationTitleHeading(props: {
  readonly title: string;
  readonly onRename?: (title: string) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.title);
  const displayTitle = isTruthyAndNotEmpty(props.title.trim())
    ? props.title
    : m.untitledConversation();

  useEffect(() => {
    if (!editing) {
      setDraft(props.title);
    }
  }, [props.title, editing]);

  if (props.onRename === undefined || !editing) {
    return (
      <h1 className="session-conversation-title">
        <button
          type="button"
          className="session-conversation-title-button"
          onClick={() => {
            if (props.onRename === undefined) {
              return;
            }
            setDraft(props.title);
            setEditing(true);
          }}
          disabled={props.onRename === undefined}
          title={props.onRename === undefined ? undefined : m.renameConversation()}
          aria-label={
            props.onRename === undefined
              ? displayTitle
              : `${displayTitle} — ${m.renameConversation()}`
          }
        >
          {displayTitle}
        </button>
      </h1>
    );
  }

  return (
    <form
      className="session-conversation-title-form"
      onSubmit={(event) => {
        event.preventDefault();
        const next = draft.trim();
        if (!isTruthyAndNotEmpty(next)) {
          setEditing(false);
          setDraft(props.title);
          return;
        }
        props.onRename?.(next);
        setEditing(false);
      }}
    >
      <label className="visually-hidden" htmlFor="studio-conversation-title-input">
        {m.renameConversation()}
      </label>
      <input
        id="studio-conversation-title-input"
        className="session-conversation-title-input"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          const next = draft.trim();
          if (isTruthyAndNotEmpty(next) && next !== props.title.trim()) {
            props.onRename?.(next);
          }
          setEditing(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
            setDraft(props.title);
          }
        }}
      />
      <button type="submit" className="button button-secondary">
        {m.saveConversationTitle()}
      </button>
    </form>
  );
}

const CONVERSATION_RAIL_WIDTH_PX = 240;
const CONVERSATION_RAIL_COLLAPSED_WIDTH = '1.75rem';

export type StudioShellProps = {
  readonly previewUrl: string;
  readonly previewReloadKey?: number;
  readonly previewReady?: boolean;
  readonly error: string | undefined;
  readonly chatMaximized?: boolean;
  readonly chatCollapsed?: boolean;
  readonly conversationRail?: ReactNode;
  readonly conversationRailCollapsed?: boolean;
  readonly inspectEnabled?: boolean;
  readonly previewIframeRef?: MutableRefObject<HTMLIFrameElement | null>;
  readonly onInspectSelection?: (selection: StudioInspectSelection) => void;
  readonly onPreviewFrameLoad?: () => void;
  readonly onToggleCollapse?: () => void;
  readonly pathBarDraft?: string;
  readonly pathBarVisible?: boolean;
  readonly pathBarError?: string;
  readonly pathBarCanBack?: boolean;
  readonly pathBarCanForward?: boolean;
  readonly pathBarPresets?: readonly PreviewPathPreset[];
  readonly previewDevice?: PreviewDevice;
  readonly onPathBarDraftChange?: (value: string) => void;
  readonly onPathBarFocusChange?: (focused: boolean) => void;
  readonly onPathBarApply?: () => void;
  readonly onPathBarBack?: () => void;
  readonly onPathBarForward?: () => void;
  readonly onPathBarPreset?: (path: string) => void;
  readonly onPreviewLocationPush?: (path: string) => void;
  readonly children: ReactNode;
};

type PointerDrag = {
  readonly startX: number;
  readonly startWidth: number;
};

type StudioStageStyle = {
  readonly gridTemplateColumns: string;
};

export function formatStudioApiError(error: unknown): string {
  if (error instanceof StudioApiError && error.kind === 'unreachable') {
    return m.apiUnreachable();
  }
  if (error instanceof StudioApiError && isTruthyAndNotEmpty(error.message)) {
    return error.message;
  }
  return m.apiCallFailed();
}

export function isUnauthorizedStudioError(error: unknown): boolean {
  return error instanceof StudioApiError && error.status === 401;
}

type SetError = (value: string | undefined) => void;

function isSessionAccessForbidden(reason: unknown): boolean {
  return reason instanceof StudioApiError && reason.status === 403;
}

function reportStudioError(reason: unknown, setError: SetError, onUnauthorized: () => void): void {
  if (isUnauthorizedStudioError(reason)) {
    onUnauthorized();
    return;
  }
  setError(formatStudioApiError(reason));
}

type AuthSessionResource =
  | { readonly state: 'loading' }
  | { readonly state: 'anonymous' }
  | { readonly state: 'authenticated'; readonly session: StudioPublicSession }
  | { readonly state: 'error' };

export function AnonymousPage(props: AnonymousPageProps): React.ReactElement {
  return (
    <main className="studio-login">
      <div className="studio-login-card">
        <p className="studio-login-eyebrow">{m.secureSpace()}</p>
        <h1>{m.loginTitle()}</h1>
        <p>{m.loginInstructions()}</p>
        {props.message !== undefined ? (
          <p className="alert" role="alert">
            {props.message}
          </p>
        ) : undefined}
        <a className="button button-primary" href={StudioBackendRoutes.AUTH_LOGIN}>
          {m.login()}
        </a>
      </div>
    </main>
  );
}

export function AccountBar(props: AccountBarProps): React.ReactElement {
  const {
    onOpenTargetMenu,
    onUnauthorized,
    onShowCreateScratchAppMenuChange,
    onShowDeleteScratchAppMenuChange,
    onShowPreviewPathBarChange,
    onShowPromptActivityLogChange,
    previewDevice,
    onPreviewDeviceChange,
  } = props;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const showDeviceSwitch = previewDevice !== undefined && onPreviewDeviceChange !== undefined;
  return (
    <>
      <div className="studio-account-bar">
        <div className="studio-account-bar-start">
          {onOpenTargetMenu !== undefined ? (
            <button
              className="button button-secondary studio-account-apps"
              type="button"
              onClick={onOpenTargetMenu}
              title={m.changeTarget()}
            >
              {m.changeTarget()}
            </button>
          ) : undefined}
        </div>
        <div className="studio-account-bar-center">
          {showDeviceSwitch ? (
            <PreviewDeviceSwitch device={previewDevice} onDeviceChange={onPreviewDeviceChange} />
          ) : undefined}
        </div>
        <div className="studio-account-bar-end">
          <span>{props.email}</span>
          <button
            className="button button-secondary"
            type="button"
            onClick={() => {
              setSettingsOpen(true);
            }}
            title={m.settings()}
          >
            {m.settings()}
          </button>
          <form method="post" action={StudioBackendRoutes.AUTH_LOGOUT}>
            <button className="button button-secondary" type="submit">
              {m.logout()}
            </button>
          </form>
        </div>
      </div>
      {settingsOpen ? (
        <StudioSettingsPanel
          onClose={() => {
            setSettingsOpen(false);
          }}
          onUnauthorized={onUnauthorized}
          onShowCreateScratchAppMenuChange={onShowCreateScratchAppMenuChange}
          onShowDeleteScratchAppMenuChange={onShowDeleteScratchAppMenuChange}
          onShowPreviewPathBarChange={onShowPreviewPathBarChange}
          onShowPromptActivityLogChange={onShowPromptActivityLogChange}
        />
      ) : undefined}
    </>
  );
}

type AuthenticatedFrameOptions = {
  readonly onOpenTargetMenu?: () => void;
  readonly onUnauthorized: () => void;
  readonly onShowCreateScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowDeleteScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowPreviewPathBarChange?: (enabled: boolean) => void;
  readonly onShowPromptActivityLogChange?: (enabled: boolean) => void;
  readonly showPromptActivityLog?: boolean;
  readonly previewDevice?: PreviewDevice;
  readonly onPreviewDeviceChange?: (device: PreviewDevice) => void;
};

function authenticatedFrame(
  email: string,
  child: React.ReactElement,
  options: AuthenticatedFrameOptions
): React.ReactElement {
  return (
    <div className="studio-app">
      <AccountBar
        email={email}
        onUnauthorized={options.onUnauthorized}
        onOpenTargetMenu={options.onOpenTargetMenu}
        onShowCreateScratchAppMenuChange={options.onShowCreateScratchAppMenuChange}
        onShowDeleteScratchAppMenuChange={options.onShowDeleteScratchAppMenuChange}
        onShowPreviewPathBarChange={options.onShowPreviewPathBarChange}
        onShowPromptActivityLogChange={options.onShowPromptActivityLogChange}
        previewDevice={options.previewDevice}
        onPreviewDeviceChange={options.onPreviewDeviceChange}
      />
      {child}
      {options.showPromptActivityLog === true ? (
        <PromptActivityPanel
          onClose={() => {
            writeShowPromptActivityLog(false);
            options.onShowPromptActivityLogChange?.(false);
          }}
          onUnauthorized={options.onUnauthorized}
        />
      ) : undefined}
    </div>
  );
}

export function sessionIdFromPath(pathname: string): string | undefined {
  const match = matchStudioRoute(pathname, StudioPageRoutes.SESSION(':sessionId'));
  if (!isTruthy(match)) {
    return undefined;
  }
  return match.params[0];
}

export function isAutomationsPath(pathname: string): boolean {
  return pathname === StudioPageRoutes.AUTOMATIONS;
}

export function isAppsPath(pathname: string): boolean {
  return pathname === StudioPageRoutes.APPS;
}

export function isHomePath(pathname: string): boolean {
  return isHostPreviewPath(pathname);
}

export function isRootPath(pathname: string): boolean {
  return pathname === StudioPageRoutes.ROOT || pathname === '';
}

export function historyTargetIdForPath(input: {
  readonly sessionId: string | undefined;
  readonly sessionTargetId: string | undefined;
  readonly selectedTargetId: string | undefined;
  /** Target derived from the host-preview URL (e.g. `/app/application-standard`). */
  readonly pathTargetId?: string | undefined;
}): string | undefined {
  if (input.sessionId !== undefined) {
    return input.sessionTargetId ?? input.selectedTargetId ?? input.pathTargetId;
  }
  return input.selectedTargetId ?? input.pathTargetId;
}

function navigateStudioPath(
  pathname: string,
  setPath: (value: string) => void,
  historyMode: 'push' | 'replace' = 'push'
): void {
  if (pathname === window.location.pathname) {
    setPath(pathname);
    return;
  }
  if (historyMode === 'replace') {
    window.history.replaceState({}, '', pathname);
  } else {
    window.history.pushState({}, '', pathname);
  }
  setPath(pathname);
}

function progressKindLabel(kind: AgentProgressKind): string {
  if (kind === 'thinking') {
    return m.agentThinking();
  }
  return m.agentStatus();
}

type StudioChatMessageView = SessionView['messages'][number];

function chatSpeakerLabel(message: StudioChatMessageView): string {
  if (message.role === 'assistant') {
    return m.agentName();
  }
  if (message.role === 'system') {
    return m.studioName();
  }
  if (isTruthyAndNotEmpty(message.displayName.trim())) {
    return message.displayName.trim();
  }
  return m.youName();
}

function formatChatMessageTime(createdAt: number | undefined): string | undefined {
  if (createdAt === undefined || !Number.isFinite(createdAt) || createdAt <= 0) {
    return undefined;
  }
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(createdAt));
}

export function resolveStudioPreviewUrl(
  session: SessionView | undefined,
  homePreviewUrl: string | undefined,
  sessionPreviewPath?: string
): string {
  if (session === undefined) {
    return withPreviewPath(homePreviewUrl ?? '', sessionPreviewPath);
  }
  if (session.preview.status !== 'live') {
    return '';
  }
  return withPreviewPath(session.preview.url ?? '', sessionPreviewPath);
}

function previewDeviceLabel(device: PreviewDevice): string {
  switch (device) {
    case 'tablet':
      return m.previewDeviceTablet();
    case 'mobile':
      return m.previewDeviceMobile();
    case 'desktop':
    default:
      return m.previewDeviceDesktop();
  }
}

type PreviewDeviceSwitchProps = {
  readonly device: PreviewDevice;
  readonly onDeviceChange: (device: PreviewDevice) => void;
};

function PreviewDeviceSwitch(props: PreviewDeviceSwitchProps): React.ReactElement {
  return (
    <div
      className="studio-preview-device-switch"
      role="toolbar"
      aria-label={m.previewDeviceSwitch()}
    >
      {PREVIEW_DEVICES.map((device) => {
        const label = previewDeviceLabel(device);
        return (
          <button
            key={device}
            type="button"
            className={
              props.device === device
                ? 'button button-secondary icon-button is-active'
                : 'button button-secondary icon-button'
            }
            aria-pressed={props.device === device}
            aria-label={label}
            title={label}
            onClick={() => {
              props.onDeviceChange(device);
            }}
          >
            <PreviewDeviceIcon device={device} />
          </button>
        );
      })}
    </div>
  );
}

function previewPathPresetLabel(presetId: PreviewPathPreset['id']): string {
  switch (presetId) {
    case 'catalogue':
      return m.previewPathPresetCatalogue();
    case 'diaporama':
      return m.previewPathPresetDiaporama();
    case 'home':
    default:
      return m.previewPathPresetHome();
  }
}

type PreviewDeviceIconProps = {
  readonly device: PreviewDevice;
};

function PreviewDeviceIcon(props: PreviewDeviceIconProps): React.ReactElement {
  switch (props.device) {
    case 'tablet':
      return <TabletIcon />;
    case 'mobile':
      return <MobileIcon />;
    case 'desktop':
    default:
      return <DesktopIcon />;
  }
}

export function StudioShell(props: StudioShellProps): React.ReactElement {
  const {
    previewUrl,
    previewReloadKey,
    previewReady: previewReadyProp,
    error,
    chatMaximized: chatMaximizedProp,
    chatCollapsed: chatCollapsedProp,
    conversationRail,
    conversationRailCollapsed: conversationRailCollapsedProp,
    inspectEnabled: inspectEnabledProp,
    previewIframeRef,
    onInspectSelection,
    onPreviewFrameLoad,
    onToggleCollapse,
    pathBarDraft,
    pathBarVisible: pathBarVisibleProp,
    pathBarError,
    pathBarCanBack,
    pathBarCanForward,
    pathBarPresets,
    previewDevice: previewDeviceProp,
    onPathBarDraftChange,
    onPathBarFocusChange,
    onPathBarApply,
    onPathBarBack,
    onPathBarForward,
    onPathBarPreset,
    onPreviewLocationPush,
    children,
  } = props;
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH);
  const dragRef = useRef<PointerDrag | undefined>(undefined);
  const previewPaneRef = useRef<HTMLDivElement>(null);
  const localIframeRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = previewIframeRef ?? localIframeRef;
  const chatMaximized = chatMaximizedProp === true;
  const chatCollapsed = chatCollapsedProp === true && !chatMaximized;
  const showConversationRail = conversationRail !== undefined && !chatMaximized;
  const conversationRailCollapsed = conversationRailCollapsedProp === true || !showConversationRail;
  let conversationRailColumn: string | undefined;
  if (showConversationRail && conversationRailCollapsed) {
    conversationRailColumn = CONVERSATION_RAIL_COLLAPSED_WIDTH;
  } else if (showConversationRail) {
    conversationRailColumn = `${String(CONVERSATION_RAIL_WIDTH_PX)}px`;
  }
  const inspectEnabled = inspectEnabledProp === true;
  const previewDevice = previewDeviceProp ?? 'desktop';
  const showResize = !chatMaximized && !chatCollapsed;
  let gridTemplateColumns = `minmax(0, 1fr) 0.55rem ${String(chatWidth)}px`;
  if (chatMaximized) {
    gridTemplateColumns = 'minmax(0, 1fr)';
  } else if (chatCollapsed && conversationRailColumn === undefined) {
    gridTemplateColumns = `minmax(0, 1fr) ${CHAT_COLLAPSED_RAIL_WIDTH}`;
  } else if (chatCollapsed && conversationRailColumn !== undefined) {
    gridTemplateColumns = `${conversationRailColumn} minmax(0, 1fr) ${CHAT_COLLAPSED_RAIL_WIDTH}`;
  } else if (conversationRailColumn !== undefined) {
    gridTemplateColumns = `${conversationRailColumn} minmax(0, 1fr) 0.55rem ${String(chatWidth)}px`;
  }
  const stageStyle: StudioStageStyle = {
    gridTemplateColumns,
  };
  const previewReady = previewReadyProp !== false;
  let stageClassName = 'studio-stage';
  if (chatMaximized) {
    stageClassName = 'studio-stage is-chat-maximized';
  } else if (chatCollapsed) {
    stageClassName = 'studio-stage is-chat-collapsed';
  }
  if (showConversationRail) {
    stageClassName = `${stageClassName} has-conversation-rail`;
  }
  const chatClassName = chatCollapsed ? 'studio-chat is-collapsed' : 'studio-chat';
  const collapseLabel = chatCollapsed ? m.expandChat() : m.collapseChat();
  const iframeSrc = withStudioReloadQuery(previewUrl, previewReloadKey);
  useEffect(() => {
    postToPreviewFrame(iframeRef.current, studioInspectRequest(inspectEnabled), previewUrl);
    if (!inspectEnabled) {
      return () => undefined;
    }
    const timer = window.setInterval(() => {
      postToPreviewFrame(iframeRef.current, studioInspectRequest(true), previewUrl);
    }, 500);
    return () => {
      window.clearInterval(timer);
    };
  }, [inspectEnabled, previewReloadKey, previewReady, previewUrl, iframeRef]);

  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      if (!isPreviewMessageOrigin(event.origin, previewUrl)) {
        return;
      }
      if (typeof event.data !== 'object' || event.data === null) {
        return;
      }
      const record = event.data as Record<string, unknown>;
      if (record.type === STUDIO_INSPECT_READY_TYPE && isStudioInspectReady(record)) {
        postToPreviewFrame(iframeRef.current, studioInspectRequest(inspectEnabled), previewUrl);
        return;
      }
      if (record.type === STUDIO_LOCATION_PUSH_TYPE && isStudioLocationPush(record)) {
        onPreviewLocationPush?.(record.path);
        return;
      }
      if (record.type !== STUDIO_INSPECT_SELECTION_TYPE || !isStudioInspectSelection(record)) {
        return;
      }
      onInspectSelection?.(record);
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [inspectEnabled, onInspectSelection, onPreviewLocationPush, previewUrl, iframeRef]);

  const showPathBar = !chatMaximized && pathBarDraft !== undefined && pathBarVisibleProp === true;
  let previewPaneClassName = 'studio-preview-pane';
  if (showPathBar) {
    previewPaneClassName = 'studio-preview-pane has-path-bar';
  }

  return (
    <div className={stageClassName} style={stageStyle}>
      {showConversationRail ? conversationRail : undefined}
      <div className={previewPaneClassName} ref={previewPaneRef}>
        {showPathBar ? (
          <form
            className="studio-preview-path-bar"
            onSubmit={(event) => {
              event.preventDefault();
              onPathBarApply?.();
            }}
          >
            <div className="studio-preview-path-nav">
              <button
                type="button"
                className="button button-secondary icon-button"
                aria-label={m.previewPathBack()}
                title={m.previewPathBack()}
                disabled={pathBarCanBack !== true}
                onClick={() => {
                  onPathBarBack?.();
                }}
              >
                <ChevronLeftIcon />
              </button>
              <button
                type="button"
                className="button button-secondary icon-button"
                aria-label={m.previewPathForward()}
                title={m.previewPathForward()}
                disabled={pathBarCanForward !== true}
                onClick={() => {
                  onPathBarForward?.();
                }}
              >
                <ChevronRightIcon />
              </button>
            </div>
            <label className="visually-hidden" htmlFor="studio-preview-path-input">
              {m.previewPathLabel()}
            </label>
            <input
              id="studio-preview-path-input"
              className="studio-preview-path-field"
              type="text"
              value={pathBarDraft}
              aria-invalid={pathBarError !== undefined}
              aria-describedby={
                pathBarError === undefined ? undefined : 'studio-preview-path-error'
              }
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => {
                onPathBarDraftChange?.(event.target.value);
              }}
              onFocus={() => {
                onPathBarFocusChange?.(true);
              }}
              onBlur={() => {
                onPathBarFocusChange?.(false);
              }}
            />
            <button type="submit" className="button button-secondary">
              {m.previewPathApply()}
            </button>
            {pathBarPresets !== undefined && pathBarPresets.length > 0 ? (
              <div
                className="studio-preview-path-presets"
                role="group"
                aria-label={m.previewPathPresets()}
              >
                {pathBarPresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="button button-secondary"
                    onClick={() => {
                      onPathBarPreset?.(preset.path);
                    }}
                  >
                    {previewPathPresetLabel(preset.id)}
                  </button>
                ))}
              </div>
            ) : undefined}
            {pathBarError === undefined ? undefined : (
              <p id="studio-preview-path-error" className="studio-preview-path-error" role="alert">
                {pathBarError}
              </p>
            )}
          </form>
        ) : undefined}
        <div
          className={previewDeviceFrameClassName(previewDevice)}
          style={previewDeviceFrameStyle(previewDevice)}
        >
          {previewReady ? (
            <iframe
              ref={iframeRef}
              className="studio-site"
              title={m.previewRegion()}
              src={iframeSrc}
              key={previewReloadKey}
              onLoad={() => {
                postToPreviewFrame(
                  iframeRef.current,
                  studioInspectRequest(inspectEnabled),
                  previewUrl
                );
                onPreviewFrameLoad?.();
              }}
            />
          ) : (
            <div className="studio-site studio-site-pending" role="status">
              {m.previewRestoring()}
            </div>
          )}
        </div>
      </div>
      {showResize ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={m.resizeChat()}
          aria-valuenow={chatWidth}
          aria-valuemin={MIN_CHAT_WIDTH}
          aria-valuemax={MAX_CHAT_WIDTH}
          tabIndex={0}
          className="studio-resize"
          onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            dragRef.current = { startX: event.clientX, startWidth: chatWidth };
          }}
          onPointerMove={(event: PointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current;
            if (drag === undefined) {
              return;
            }
            setChatWidth(nextChatWidthFromDrag(drag.startWidth, drag.startX, event.clientX));
          }}
          onPointerUp={() => {
            dragRef.current = undefined;
          }}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            const nextWidth = nextChatWidthFromKey(chatWidth, event.key);
            if (nextWidth === undefined) {
              return;
            }
            event.preventDefault();
            setChatWidth(nextWidth);
          }}
        />
      ) : undefined}
      <aside className={chatClassName} aria-label={m.chatRegion()}>
        <button
          type="button"
          className="studio-chat-collapse-toggle"
          onClick={() => {
            onToggleCollapse?.();
          }}
          aria-expanded={!chatCollapsed}
          aria-label={collapseLabel}
          title={collapseLabel}
        >
          {chatCollapsed ? <ExpandChatIcon /> : <CollapseChatIcon />}
        </button>
        {chatCollapsed ? undefined : (
          <>
            {error !== undefined ? (
              <p className="alert" role="alert">
                {error}
              </p>
            ) : undefined}
            {children}
          </>
        )}
      </aside>
    </div>
  );
}

type ChatPanelToolbarProps = {
  readonly mergeRequestUrl: string | undefined;
  readonly agentBusy: boolean;
  readonly showSessionActions: boolean;
  readonly favorited?: boolean;
  readonly canDeploy?: boolean;
  readonly isDeployed?: boolean;
  readonly deployBusy?: boolean;
  readonly currentScratchSlug?: string;
  readonly deployedSlugs?: readonly string[];
  readonly chatMaximized: boolean;
  readonly conversationRailOpen: boolean;
  readonly onOpenMergeRequest: () => void;
  readonly onSyncSessionBase: () => void;
  readonly onDeploySession?: (request: DeploySessionRequest) => void;
  readonly onCopyLink: () => void;
  readonly onAbandon: () => void;
  readonly onToggleFavorite?: () => void;
  readonly onToggleMaximize: () => void;
  readonly onToggleConversationRail: () => void;
  readonly sharing?: {
    readonly visibility: SessionVisibility;
    readonly sharedWithEmails: readonly string[];
    readonly canManage: boolean;
    readonly busy?: boolean;
    readonly onVisibilityChange: (visibility: SessionVisibility) => void;
    readonly onSharedWithEmailsChange: (emails: readonly string[]) => void;
  };
};

const DEFAULT_STUDIO_PUBLIC_HOST = 'studio.stimcar.site';

function studioPublicHostForDeployPreview(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_STUDIO_PUBLIC_HOST;
  }
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return DEFAULT_STUDIO_PUBLIC_HOST;
  }
  const parts = host.split('.').filter((part) => part.length > 0);
  if (parts.length >= 3 && parts[0] === 'studio') {
    return host;
  }
  if (parts.length >= 4 && parts[1] === 'studio') {
    return parts.slice(1).join('.');
  }
  return DEFAULT_STUDIO_PUBLIC_HOST;
}

function scratchSlugFromTargetId(targetId: string): string | undefined {
  const prefix = 'scratch-app:';
  if (!targetId.startsWith(prefix)) {
    return undefined;
  }
  const slug = targetId.slice(prefix.length).trim();
  return slug === '' ? undefined : slug;
}

function deploySlugAvailabilityError(
  slug: string,
  deployedSlugs: readonly string[]
): string | undefined {
  const reservedOrInvalid = tryAssertDeployableScratchSlug(slug);
  if (reservedOrInvalid !== undefined) {
    if (/Reserved/i.test(reservedOrInvalid)) {
      return m.deploySlugReservedHint();
    }
    return m.deploySlugInvalidHint();
  }
  if (deployedSlugs.includes(slug)) {
    return m.deploySlugTakenHint();
  }
  return undefined;
}

function openAbandonConfirmDialog(dialog: HTMLDialogElement | null): void {
  if (dialog === null || dialog.open) {
    return;
  }
  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
}

function deployActionLabel(deployBusy: boolean, isDeployed: boolean): string {
  if (deployBusy) {
    return m.deploySessionWorking();
  }
  if (isDeployed) {
    return m.redeploySessionAction();
  }
  return m.deploySessionAction();
}

function ChatPanelToolbar(props: ChatPanelToolbarProps): React.ReactElement {
  const [abandonConfirmOpen, setAbandonConfirmOpen] = useState(false);
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployMode, setDeployMode] = useState<'replace' | 'new'>('replace');
  const [deployNewSlug, setDeployNewSlug] = useState('');
  const publishMenuRef = useRef<HTMLDivElement>(null);
  const mergeRequestLabel =
    props.mergeRequestUrl === undefined ? m.openMergeRequest() : m.updateMergeRequest();
  const maximizeLabel = props.chatMaximized ? m.restoreChat() : m.maximizeChat();
  const conversationRailLabel = props.conversationRailOpen
    ? m.collapseConversationRail()
    : m.expandConversationRail();
  const showPublishMenu = props.canDeploy !== true;
  const canDeploy = props.canDeploy === true && props.onDeploySession !== undefined;
  const deployedSlugs = props.deployedSlugs ?? [];
  const currentScratchSlug = (props.currentScratchSlug ?? '').trim().toLowerCase();
  const normalizedNewSlug = slugifyScratchAppName(deployNewSlug) ?? '';
  let newSlugError: string | undefined;
  if (deployMode === 'new' && normalizedNewSlug !== '') {
    newSlugError = deploySlugAvailabilityError(normalizedNewSlug, deployedSlugs);
  } else if (deployMode === 'new' && deployNewSlug.trim() !== '') {
    newSlugError = m.deploySlugInvalidHint();
  }
  const previewSlug = deployMode === 'new' ? normalizedNewSlug : currentScratchSlug;
  const previewHostname =
    previewSlug !== '' && tryAssertDeployableScratchSlug(previewSlug) === undefined
      ? deployedAppHostname(previewSlug, studioPublicHostForDeployPreview())
      : undefined;
  const previewUrl =
    previewHostname === undefined ? undefined : deployedAppUrl(previewHostname, 'https');
  const replaceUnavailable =
    deployMode === 'replace' &&
    (currentScratchSlug === '' || tryAssertDeployableScratchSlug(currentScratchSlug) !== undefined);
  const deployConfirmDisabled =
    props.deployBusy === true ||
    replaceUnavailable ||
    (deployMode === 'new' && (normalizedNewSlug === '' || newSlugError !== undefined));

  useEffect(() => {
    if (!publishMenuOpen) {
      return undefined;
    }
    function onPointerDown(event: MouseEvent): void {
      const root = publishMenuRef.current;
      if (root === null || root.contains(event.target as Node)) {
        return;
      }
      setPublishMenuOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        setPublishMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [publishMenuOpen]);

  function requestDeploy(request: DeploySessionRequest): void {
    setDeployDialogOpen(false);
    props.onDeploySession?.(request);
  }

  function onDeployClick(): void {
    setDeployMode('replace');
    setDeployNewSlug('');
    setDeployDialogOpen(true);
  }

  const showDeliverActions = props.showSessionActions === true && (showPublishMenu || canDeploy);

  return (
    <div className="session-actions">
      {showDeliverActions ? (
        <div className="session-actions-deliver">
          {showPublishMenu ? (
            <div className="session-publish-menu" ref={publishMenuRef}>
              <button
                type="button"
                className="button button-primary button-icon-text"
                onClick={() => {
                  setPublishMenuOpen((open) => !open);
                }}
                disabled={props.agentBusy}
                aria-expanded={publishMenuOpen}
                aria-haspopup="menu"
              >
                <GitMergeIcon />
                {m.sessionPublishMenu()}
                <ChevronDownIcon />
              </button>
              {publishMenuOpen ? (
                <div className="session-publish-menu-panel" role="menu">
                  <button
                    type="button"
                    className="session-publish-menu-item"
                    role="menuitem"
                    disabled={props.agentBusy}
                    onClick={() => {
                      setPublishMenuOpen(false);
                      props.onOpenMergeRequest();
                    }}
                  >
                    <GitMergeIcon />
                    <span>{mergeRequestLabel}</span>
                  </button>
                  <button
                    type="button"
                    className="session-publish-menu-item"
                    role="menuitem"
                    disabled={props.agentBusy}
                    onClick={() => {
                      setPublishMenuOpen(false);
                      props.onSyncSessionBase();
                    }}
                  >
                    <SyncBaseIcon />
                    <span>{m.syncSessionBase()}</span>
                  </button>
                </div>
              ) : undefined}
            </div>
          ) : undefined}
          {canDeploy ? (
            <button
              type="button"
              className="button button-primary"
              disabled={props.agentBusy || props.deployBusy === true}
              onClick={onDeployClick}
            >
              {deployActionLabel(props.deployBusy === true, props.isDeployed === true)}
            </button>
          ) : undefined}
        </div>
      ) : undefined}
      <div className="session-actions-conversation">
        {props.showSessionActions ? (
          <>
            {props.sharing !== undefined ? (
              <SessionSharingPanel
                visibility={props.sharing.visibility}
                sharedWithEmails={props.sharing.sharedWithEmails}
                canManage={props.sharing.canManage}
                busy={props.sharing.busy}
                onVisibilityChange={props.sharing.onVisibilityChange}
                onSharedWithEmailsChange={props.sharing.onSharedWithEmailsChange}
              />
            ) : undefined}
            <button
              type="button"
              className="button button-secondary icon-button"
              onClick={props.onCopyLink}
              aria-label={m.copyLink()}
              title={m.copyLink()}
            >
              <LinkIcon />
            </button>
            {props.onToggleFavorite !== undefined ? (
              <button
                type="button"
                className={
                  props.favorited === true
                    ? 'button button-secondary icon-button is-active'
                    : 'button button-secondary icon-button'
                }
                onClick={props.onToggleFavorite}
                aria-pressed={props.favorited === true}
                aria-label={props.favorited === true ? m.unpinFavorite() : m.pinFavorite()}
                title={props.favorited === true ? m.unpinFavorite() : m.pinFavorite()}
              >
                <StarIcon filled={props.favorited === true} />
              </button>
            ) : undefined}
            <button
              type="button"
              className="button button-secondary icon-button"
              onClick={() => {
                setAbandonConfirmOpen(true);
              }}
              aria-label={m.abandonSession()}
              title={m.abandonSession()}
            >
              <TrashIcon />
            </button>
          </>
        ) : undefined}
        <button
          type="button"
          className="button button-secondary icon-button"
          onClick={props.onToggleConversationRail}
          aria-pressed={props.conversationRailOpen}
          aria-label={conversationRailLabel}
          title={conversationRailLabel}
        >
          <HistoryIcon />
        </button>
        <button
          type="button"
          className="button button-secondary icon-button"
          onClick={props.onToggleMaximize}
          aria-pressed={props.chatMaximized}
          aria-label={maximizeLabel}
          title={maximizeLabel}
        >
          {props.chatMaximized ? <RestoreIcon /> : <MaximizeIcon />}
        </button>
      </div>
      {abandonConfirmOpen ? (
        <dialog
          ref={openAbandonConfirmDialog}
          className="studio-confirm-dialog"
          aria-labelledby="studio-abandon-confirm-title"
          onCancel={(event) => {
            event.preventDefault();
            setAbandonConfirmOpen(false);
          }}
        >
          <p id="studio-abandon-confirm-title">{m.abandonSessionConfirm()}</p>
          <div className="studio-confirm-dialog-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => {
                setAbandonConfirmOpen(false);
              }}
            >
              {m.abandonSessionCancel()}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                setAbandonConfirmOpen(false);
                props.onAbandon();
              }}
            >
              {m.abandonSessionConfirmAction()}
            </button>
          </div>
        </dialog>
      ) : undefined}
      {deployDialogOpen ? (
        <dialog
          ref={openAbandonConfirmDialog}
          className="studio-confirm-dialog"
          aria-labelledby="studio-deploy-mode-title"
        >
          <h2 id="studio-deploy-mode-title">{m.deployModeDialogTitle()}</h2>
          <label className="studio-deploy-mode-option">
            <input
              type="radio"
              name="deploy-mode"
              checked={deployMode === 'replace'}
              onChange={() => {
                setDeployMode('replace');
              }}
            />
            {props.isDeployed === true ? m.deployModeReplaceParent() : m.deployModeThisApp()}
            {currentScratchSlug !== '' ? (
              <span className="studio-deploy-mode-slug"> ({currentScratchSlug})</span>
            ) : undefined}
          </label>
          <label className="studio-deploy-mode-option">
            <input
              type="radio"
              name="deploy-mode"
              checked={deployMode === 'new'}
              onChange={() => {
                setDeployMode('new');
              }}
            />
            {m.deployModeNewApp()}
          </label>
          {deployMode === 'new' ? (
            <label className="studio-deploy-new-slug">
              {m.deployNewSlugLabel()}
              <input
                type="text"
                value={deployNewSlug}
                placeholder={m.deployNewSlugPlaceholder()}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => {
                  setDeployNewSlug(event.target.value);
                }}
              />
            </label>
          ) : undefined}
          {newSlugError !== undefined ? (
            <p className="studio-deploy-slug-error" role="alert">
              {newSlugError}
            </p>
          ) : undefined}
          {previewUrl !== undefined ? (
            <p className="studio-deploy-preview-url">
              {m.deployPublicUrlHint({ url: previewUrl })}
            </p>
          ) : undefined}
          <div className="studio-confirm-dialog-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => {
                setDeployDialogOpen(false);
              }}
            >
              {m.deployCancelAction()}
            </button>
            <button
              type="button"
              className="button button-primary"
              disabled={deployConfirmDisabled}
              onClick={() => {
                if (deployMode === 'new') {
                  if (normalizedNewSlug === '' || newSlugError !== undefined) {
                    return;
                  }
                  requestDeploy({ mode: 'new', newSlug: normalizedNewSlug });
                  return;
                }
                requestDeploy({ mode: 'replace' });
              }}
            >
              {m.deployConfirmAction()}
            </button>
          </div>
        </dialog>
      ) : undefined}
    </div>
  );
}

type SharedComposerProps = {
  readonly draft: string;
  readonly agentBusy: boolean;
  readonly locked?: boolean;
  readonly recallingHistory: boolean;
  readonly pendingAttachments: readonly PendingChatAttachment[];
  readonly selectedComponents: readonly StudioInspectSelection[];
  readonly inspectEnabled: boolean;
  readonly dictationSupported: boolean;
  readonly dictationListening: boolean;
  readonly screenshotSupported?: boolean;
  readonly screenshotUsesSelection?: boolean;
  readonly agentModelId?: string;
  readonly agentModels?: readonly AgentModelOptionView[];
  readonly onAgentModelChange?: (modelId: string) => void;
  readonly onDraftChange: (value: string) => void;
  readonly onPickAttachments?: (files: readonly File[]) => void;
  readonly onRemoveAttachment?: (id: string) => void;
  readonly onRemoveSelection?: (index: number) => void;
  readonly onCaptureScreenshot?: () => void;
  readonly onToggleInspect: () => void;
  readonly onToggleDictation: () => void;
  readonly onSend: () => void;
  readonly onStopTurn: () => void;
  readonly onRecallOlder: () => void;
  readonly onRecallNewer: () => void;
  readonly teachEnabled?: boolean;
  readonly teachStatus?: 'idle' | 'recording' | 'finalizing' | 'ready' | 'failed';
  readonly teachBusy?: boolean;
  readonly onTeachToggle?: () => void;
};

function sharedComposer(props: SharedComposerProps): React.ReactElement {
  return (
    <ChatComposer
      draft={props.draft}
      agentBusy={props.agentBusy}
      locked={props.locked}
      recallingHistory={props.recallingHistory}
      pendingAttachments={props.pendingAttachments}
      selectedComponents={props.selectedComponents}
      inspectEnabled={props.inspectEnabled}
      dictationSupported={props.dictationSupported}
      dictationListening={props.dictationListening}
      screenshotSupported={props.screenshotSupported}
      screenshotUsesSelection={props.screenshotUsesSelection}
      agentModelId={props.agentModelId}
      agentModels={props.agentModels}
      onAgentModelChange={props.onAgentModelChange}
      onDraftChange={props.onDraftChange}
      onPickAttachments={props.onPickAttachments}
      onRemoveAttachment={props.onRemoveAttachment}
      onRemoveSelection={props.onRemoveSelection}
      onCaptureScreenshot={props.onCaptureScreenshot}
      onToggleInspect={props.onToggleInspect}
      onToggleDictation={props.onToggleDictation}
      onSend={props.onSend}
      onStopTurn={props.onStopTurn}
      onRecallOlder={props.onRecallOlder}
      onRecallNewer={props.onRecallNewer}
      teachEnabled={props.teachEnabled}
      teachStatus={props.teachStatus}
      teachBusy={props.teachBusy}
      onTeachToggle={props.onTeachToggle}
    />
  );
}

export type { PickerSectionId } from './pickerSection.js';
export { resolveInitialPickerSection } from './pickerSection.js';

type PickerNavItem = {
  readonly id: PickerSectionId;
  readonly label: string;
  readonly className: string;
};

function favoriteTilePreview(favorite: FavoriteSessionSummaryView): React.ReactElement {
  if (favorite.livePreviewUrl !== undefined) {
    return (
      <iframe
        className="studio-target-preview-frame"
        src={favorite.livePreviewUrl}
        title={m.targetPreviewLabel({ label: favorite.targetLabel })}
        loading="lazy"
        tabIndex={-1}
      />
    );
  }
  if (favorite.thumbnailUrl !== undefined) {
    return <img className="studio-target-preview-frame" src={favorite.thumbnailUrl} alt="" />;
  }
  return <span className="studio-target-preview-fallback">{m.targetPreviewUnavailable()}</span>;
}

function favoriteTileTitle(favorite: FavoriteSessionSummaryView): string {
  if (isTruthyAndNotEmpty(favorite.title.trim())) {
    return favorite.title;
  }
  return m.untitledConversation();
}

export function TargetPickerPage(props: TargetPickerPageProps): React.ReactElement {
  const creatingScratchApp = props.creatingScratchApp === true;
  const canCreateScratchApp = props.canCreateScratchApp === true;
  const canDeleteScratchApp = props.canDeleteScratchApp === true;
  const { deletingScratchAppSlug } = props;
  const deployments = props.deployments ?? [];
  const favorites = props.favorites ?? [];
  const lastIndependent = props.lastIndependentSession ?? null;
  const hasFavorites = favorites.length > 0;
  const hasContinueLast = lastIndependent !== null;
  const quickStartDraft = props.quickStartDraft ?? '';
  const quickStartBusy = props.quickStartBusy === true;
  const hostBusyMessage = hostBusyBannerMessage(props.hostBusySessionCount ?? 0);
  const quickStartLocked = creatingScratchApp || quickStartBusy;
  const partitioned = partitionPickerTargets(props.targets);
  const [composingNewApp, setComposingNewApp] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [slugError, setSlugError] = useState<string | undefined>(undefined);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<
    StudioTargetSummaryView | undefined
  >(undefined);
  const [openTileMenuId, setOpenTileMenuId] = useState<string | undefined>(undefined);
  const [revertSlug, setRevertSlug] = useState<string | undefined>(undefined);
  const [revertCommits, setRevertCommits] = useState<readonly DeployedAppCommitSummaryView[]>([]);
  const [revertBusy, setRevertBusy] = useState(false);
  const [revertError, setRevertError] = useState<string | undefined>(undefined);
  const [activeSection, setActiveSection] = useState<PickerSectionId>(
    () =>
      props.initialSection ??
      resolveInitialPickerSection({
        hasFavorites,
        websiteCount: partitioned.website.length,
        independentCount: partitioned.independent.length,
        canCreateScratchApp,
        deploymentCount: deployments.length,
      })
  );
  /** Until the designer clicks a rail tab, keep following resolveInitial as data loads. */
  const userPickedSectionRef = useRef(props.initialSection !== undefined);
  const [titleIndex, setTitleIndex] = useState(() =>
    initialWelcomeTitleIndex(Date.now(), WELCOME_TITLE_KINDS.length)
  );
  const [titleVisible, setTitleVisible] = useState(true);
  const [railOrientation, setRailOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const derivedSlug = slugifyScratchAppName(newAppName);
  const welcomeTitle = welcomeTitleFor(
    WELCOME_TITLE_KINDS[titleIndex] ?? 'improve',
    props.firstName
  );

  const navItems: readonly PickerNavItem[] = [
    ...(hasFavorites
      ? [
          {
            id: 'favorites' as const,
            label: m.favoritesSectionLabel(),
            className: 'studio-picker-favorites',
          },
        ]
      : []),
    {
      id: 'start',
      label: m.startSectionLabel(),
      className: 'studio-picker-start',
    },
    {
      id: 'website',
      label: m.websiteSectionLabel(),
      className: 'studio-picker-website',
    },
    {
      id: 'independent',
      label: m.independentAppsSectionLabel(),
      className: 'studio-picker-independent',
    },
    {
      id: 'deployed',
      label: m.deployedAppsSectionLabel(),
      className: 'studio-picker-deployed',
    },
  ];
  const sectionIds = navItems.map((item) => item.id);

  function selectSection(
    section: PickerSectionId,
    options?: { readonly userPicked?: boolean }
  ): void {
    if (options?.userPicked === true) {
      userPickedSectionRef.current = true;
    }
    writeStoredPickerSectionId(section);
    setActiveSection(section);
    if (section !== 'independent') {
      setComposingNewApp(false);
      setNewAppName('');
      setSlugError(undefined);
    }
  }

  function renderTargetTile(target: StudioTargetSummaryView): React.ReactElement {
    const slug = parseScratchAppSlugFromTargetId(target.id);
    const isDefaultIndependent = slug === DEFAULT_HOST_SCRATCH_APP_SLUG;
    const showDelete =
      canDeleteScratchApp &&
      slug !== undefined &&
      !isDefaultIndependent &&
      props.onDeleteScratchApp !== undefined;
    // Only scratch tiles share a slug; public-web ids parse to undefined and must
    // not compare equal to an idle deletingScratchAppSlug (also undefined).
    const deleteBusy = slug !== undefined && deletingScratchAppSlug === slug;
    const optionClassName = isDefaultIndependent
      ? 'studio-target-option studio-target-option-independent studio-target-option-starter'
      : 'studio-target-option studio-target-option-independent';
    const tileMenuId = `target:${target.id}`;
    return (
      <li key={target.id}>
        <div className={optionClassName}>
          <button
            className="studio-target-option-main"
            type="button"
            disabled={creatingScratchApp || deleteBusy}
            onClick={() => {
              props.onSelectTarget(target.id);
            }}
          >
            <span className="studio-target-preview" aria-hidden="true">
              {target.previewAvailable ? (
                <iframe
                  className="studio-target-preview-frame"
                  src={target.hostPreviewUrl}
                  title={m.targetPreviewLabel({ label: target.label })}
                  loading="lazy"
                  tabIndex={-1}
                />
              ) : (
                <span className="studio-target-preview-fallback">
                  {m.targetPreviewUnavailable()}
                </span>
              )}
            </span>
            <span className="studio-target-option-copy">
              <span className="studio-target-option-label">
                {target.label}
                {isDefaultIndependent ? (
                  <span className="studio-target-starter-badge">
                    {m.defaultIndependentAppBadge()}
                  </span>
                ) : undefined}
                {target.resumeSessionId !== undefined ? (
                  <span className="studio-target-wip-badge">{m.targetWipBadge()}</span>
                ) : undefined}
              </span>
              <span className="studio-target-option-description">{target.description}</span>
              <span className="studio-target-option-action">
                {(() => {
                  if (target.resumeSessionId !== undefined) {
                    return m.targetResumeAction();
                  }
                  if (isDefaultIndependent) {
                    return m.defaultIndependentAppAction();
                  }
                  return m.targetOpenAction();
                })()}
              </span>
            </span>
          </button>
          {showDelete ? (
            <TargetTileOverflowMenu
              menuId={tileMenuId}
              open={openTileMenuId === tileMenuId}
              disabled={creatingScratchApp || deleteBusy}
              onOpenChange={(nextOpen) => {
                setOpenTileMenuId(nextOpen ? tileMenuId : undefined);
              }}
            >
              <TargetTileOverflowMenuItem
                danger
                disabled={creatingScratchApp || deleteBusy}
                onClick={() => {
                  setOpenTileMenuId(undefined);
                  setDeleteConfirmTarget(target);
                }}
              >
                {deleteBusy ? m.deleteScratchAppWorking() : m.deleteScratchAppAction()}
              </TargetTileOverflowMenuItem>
            </TargetTileOverflowMenu>
          ) : undefined}
        </div>
      </li>
    );
  }

  useEffect(() => {
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || WELCOME_TITLE_KINDS.length <= 1) {
      return () => undefined;
    }
    let fadeTimer: number | undefined;
    const timer = window.setInterval(() => {
      setTitleVisible(false);
      fadeTimer = window.setTimeout(() => {
        setTitleIndex((current) => nextWelcomeTitleIndex(current, WELCOME_TITLE_KINDS.length));
        setTitleVisible(true);
      }, 320);
    }, WELCOME_TITLE_ROTATE_MS);
    return () => {
      window.clearInterval(timer);
      if (fadeTimer !== undefined) {
        window.clearTimeout(fadeTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return () => undefined;
    }
    const query = window.matchMedia('(max-width: 40rem)');
    function syncOrientation(): void {
      setRailOrientation(query.matches ? 'horizontal' : 'vertical');
    }
    syncOrientation();
    query.addEventListener('change', syncOrientation);
    return () => {
      query.removeEventListener('change', syncOrientation);
    };
  }, []);

  const sectionIdsKey = sectionIds.join('|');
  const preferredSection = resolveInitialPickerSection({
    hasFavorites,
    websiteCount: partitioned.website.length,
    independentCount: partitioned.independent.length,
    canCreateScratchApp,
    deploymentCount: deployments.length,
  });

  useEffect(() => {
    // Favorites/targets/deployments load async after /apps mount. Until the user
    // picks a rail tab, keep the panel on resolveInitial (favorites when ready).
    if (userPickedSectionRef.current) {
      if (!sectionIds.includes(activeSection)) {
        selectSection(preferredSection);
      }
      return;
    }
    if (activeSection !== preferredSection) {
      selectSection(preferredSection);
    }
  }, [
    sectionIds,
    sectionIdsKey,
    activeSection,
    preferredSection,
    hasFavorites,
    canCreateScratchApp,
    deployments.length,
  ]);

  function submitNewScratchApp(): void {
    if (!canCreateScratchApp || creatingScratchApp) {
      return;
    }
    if (derivedSlug === undefined) {
      setSlugError(m.newScratchAppSlugInvalid());
      return;
    }
    setSlugError(undefined);
    props.onCreateScratchApp(derivedSlug);
  }

  function handleFavoriteClick(event: React.MouseEvent<HTMLAnchorElement>, urlPath: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (creatingScratchApp) {
      return;
    }
    props.onSelectFavorite?.(urlPath);
  }

  function renderQuickStartForm(): React.ReactElement | undefined {
    if (props.onQuickStart === undefined) {
      return undefined;
    }
    const canSubmit = !quickStartLocked && isTruthyAndNotEmpty(quickStartDraft.trim());
    return (
      <form
        className="studio-quick-start"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) {
            return;
          }
          props.onQuickStart?.();
        }}
      >
        <label className="visually-hidden" htmlFor="studio-quick-start-input">
          {m.quickStartLabel()}
        </label>
        <div className="studio-quick-start-shell">
          <textarea
            id="studio-quick-start-input"
            className="studio-quick-start-input"
            rows={2}
            autoComplete="off"
            disabled={quickStartLocked}
            placeholder={m.quickStartPlaceholder()}
            value={quickStartDraft}
            onChange={(event) => {
              props.onQuickStartDraftChange?.(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (canSubmit) {
                  props.onQuickStart?.();
                }
              }
            }}
          />
          <button
            className="composer-send studio-quick-start-send"
            type="submit"
            disabled={!canSubmit}
            aria-label={quickStartBusy ? m.quickStartWorking() : m.sendMessage()}
            title={quickStartBusy ? m.quickStartWorking() : m.sendMessage()}
          >
            <SendIcon />
          </button>
        </div>
      </form>
    );
  }

  function renderSection(section: PickerSectionId): React.ReactElement {
    switch (section) {
      case 'favorites':
        return (
          <section
            className="studio-target-explore studio-picker-favorites"
            aria-label={m.favoritesSectionLabel()}
          >
            <h2 className="studio-picker-panel-title">{m.favoritesSectionLabel()}</h2>
            <ul className="studio-target-list">
              {favorites.map((favorite) => (
                <li key={favorite.sessionId}>
                  <a
                    className="studio-target-option"
                    href={favorite.urlPath}
                    aria-disabled={creatingScratchApp}
                    onClick={(event) => {
                      handleFavoriteClick(event, favorite.urlPath);
                    }}
                  >
                    <span className="studio-target-preview" aria-hidden="true">
                      {favoriteTilePreview(favorite)}
                    </span>
                    <span className="studio-target-option-copy">
                      <span className="studio-target-option-label">
                        {favoriteTileTitle(favorite)}
                        <span className="studio-target-wip-badge">{m.favoriteBadge()}</span>
                      </span>
                      <span className="studio-target-option-description">
                        {favorite.targetLabel}
                      </span>
                      <span className="studio-target-option-action">{m.favoriteOpenAction()}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        );
      case 'start':
        return (
          <section
            className="studio-target-explore studio-picker-start"
            aria-label={m.startSectionLabel()}
          >
            <div className="studio-picker-start-center">
              <h2 className="studio-picker-start-greeting">{m.startSectionTitle()}</h2>
              {hostBusyMessage !== undefined ? (
                <p className="studio-host-busy-banner" role="status" aria-live="polite">
                  {hostBusyMessage}
                </p>
              ) : undefined}
              {renderQuickStartForm()}
              {hasContinueLast && lastIndependent !== null ? (
                <div className="studio-continue-last" aria-label={m.continueLastAppSectionLabel()}>
                  <button
                    className="studio-continue-last-link"
                    type="button"
                    disabled={creatingScratchApp}
                    onClick={() => {
                      props.onSelectLastIndependent?.(lastIndependent.urlPath);
                    }}
                  >
                    <span className="studio-continue-last-link-label">
                      {m.continueLastAppAction()}
                    </span>
                    <span className="studio-continue-last-link-title">
                      {isTruthyAndNotEmpty(lastIndependent.title.trim())
                        ? lastIndependent.title
                        : lastIndependent.targetLabel}
                    </span>
                  </button>
                </div>
              ) : undefined}
            </div>
          </section>
        );
      case 'website':
        return (
          <section
            className="studio-target-explore studio-picker-website"
            aria-label={m.websiteSectionLabel()}
          >
            <h2 className="studio-picker-panel-title">{m.websiteSectionLabel()}</h2>
            {partitioned.website.length === 0 ? (
              <p className="studio-welcome-empty">{m.websiteSectionEmpty()}</p>
            ) : undefined}
            <ul className="studio-target-list">{partitioned.website.map(renderTargetTile)}</ul>
          </section>
        );
      case 'independent':
        return (
          <section
            className="studio-target-explore studio-picker-independent"
            aria-label={m.independentAppsSectionLabel()}
          >
            <h2 className="studio-picker-panel-title">{m.independentAppsSectionLabel()}</h2>
            {partitioned.independent.length === 0 && !canCreateScratchApp ? (
              <p className="studio-welcome-empty">{m.independentAppsSectionEmpty()}</p>
            ) : undefined}
            <ul className="studio-target-list">
              {partitioned.independent.map(renderTargetTile)}
              {canCreateScratchApp ? (
                <li>
                  {composingNewApp ? (
                    <form
                      className="studio-target-option studio-target-create-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        submitNewScratchApp();
                      }}
                    >
                      <span
                        className="studio-target-preview studio-target-preview-create"
                        aria-hidden="true"
                      >
                        <span className="studio-target-preview-fallback">+</span>
                      </span>
                      <span className="studio-target-option-copy studio-target-create-fields">
                        <label className="studio-target-create-label" htmlFor="scratch-app-name">
                          {m.newScratchAppNameLabel()}
                        </label>
                        <input
                          id="scratch-app-name"
                          className="studio-target-create-input"
                          type="text"
                          autoComplete="off"
                          disabled={creatingScratchApp}
                          placeholder={m.newScratchAppNamePlaceholder()}
                          value={newAppName}
                          onChange={(event) => {
                            setNewAppName(event.target.value);
                            setSlugError(undefined);
                          }}
                        />
                        {derivedSlug !== undefined ? (
                          <span className="studio-target-create-slug">
                            {m.newScratchAppSlugHint({ slug: derivedSlug })}
                          </span>
                        ) : undefined}
                        {slugError !== undefined ? (
                          <span className="studio-target-create-error" role="alert">
                            {slugError}
                          </span>
                        ) : undefined}
                        <span className="studio-target-create-actions">
                          <button
                            className="button button-secondary"
                            type="button"
                            disabled={creatingScratchApp}
                            onClick={() => {
                              setComposingNewApp(false);
                              setNewAppName('');
                              setSlugError(undefined);
                            }}
                          >
                            {m.newScratchAppCancel()}
                          </button>
                          <button
                            className="button button-primary"
                            type="submit"
                            disabled={creatingScratchApp}
                          >
                            {creatingScratchApp
                              ? m.newScratchAppCreating()
                              : m.newScratchAppAction()}
                          </button>
                        </span>
                      </span>
                    </form>
                  ) : (
                    <button
                      className="studio-target-option studio-target-option-create"
                      type="button"
                      disabled={creatingScratchApp}
                      onClick={() => {
                        setComposingNewApp(true);
                        setSlugError(undefined);
                      }}
                    >
                      <span
                        className="studio-target-preview studio-target-preview-create"
                        aria-hidden="true"
                      >
                        <span className="studio-target-preview-fallback">+</span>
                      </span>
                      <span className="studio-target-option-copy">
                        <span className="studio-target-option-label">{m.newScratchAppLabel()}</span>
                        <span className="studio-target-option-description">
                          {m.newScratchAppDescription()}
                        </span>
                        <span className="studio-target-option-action">
                          {m.newScratchAppAction()}
                        </span>
                      </span>
                    </button>
                  )}
                </li>
              ) : undefined}
            </ul>
          </section>
        );
      case 'deployed':
      default:
        return (
          <section
            className="studio-target-explore studio-picker-deployed"
            aria-label={m.deployedAppsSectionLabel()}
          >
            <h2 className="studio-picker-panel-title">{m.deployedAppsSectionLabel()}</h2>
            {deployments.length === 0 ? (
              <p className="studio-welcome-empty">{m.deployedAppsEmpty()}</p>
            ) : undefined}
            <ul className="studio-target-list">
              {deployments.map((deployment) => {
                const tileMenuId = `deployed:${deployment.id}`;
                return (
                  <li key={deployment.id}>
                    <div className="studio-target-option studio-target-option-deployed">
                      <button
                        className="studio-target-option-main"
                        type="button"
                        disabled={creatingScratchApp}
                        onClick={() => {
                          props.onSelectDeployment?.(deployment);
                        }}
                      >
                        <span className="studio-target-preview" aria-hidden="true">
                          <span className="studio-target-preview-fallback">
                            {m.targetPreviewUnavailable()}
                          </span>
                        </span>
                        <span className="studio-target-option-copy">
                          <span className="studio-target-option-label">{deployment.label}</span>
                          <span className="studio-target-option-description">
                            {deployment.hostname}
                          </span>
                          <span className="studio-target-option-action">
                            {m.deployedAppOpenAction()}
                          </span>
                        </span>
                      </button>
                      <TargetTileOverflowMenu
                        menuId={tileMenuId}
                        open={openTileMenuId === tileMenuId}
                        disabled={creatingScratchApp}
                        onOpenChange={(nextOpen) => {
                          setOpenTileMenuId(nextOpen ? tileMenuId : undefined);
                        }}
                      >
                        <TargetTileOverflowMenuItem
                          disabled={creatingScratchApp}
                          onClick={() => {
                            setOpenTileMenuId(undefined);
                            props.onEditDeployment?.(deployment);
                          }}
                        >
                          {m.deployedAppEditAction()}
                        </TargetTileOverflowMenuItem>
                        <TargetTileOverflowMenuItem
                          disabled={creatingScratchApp || revertBusy}
                          onClick={() => {
                            setOpenTileMenuId(undefined);
                            props.onStopDeployment?.(deployment);
                          }}
                        >
                          {m.deployedAppStopAction()}
                        </TargetTileOverflowMenuItem>
                        <TargetTileOverflowMenuItem
                          disabled={creatingScratchApp || revertBusy}
                          onClick={() => {
                            setOpenTileMenuId(undefined);
                            setRevertError(undefined);
                            setRevertSlug(deployment.slug);
                            setRevertCommits([]);
                            setRevertBusy(true);
                            runBackground(
                              listDeploymentCommits(deployment.slug).then(
                                (commits) => {
                                  setRevertCommits(commits);
                                  setRevertBusy(false);
                                },
                                (reason: unknown) => {
                                  setRevertBusy(false);
                                  setRevertError(
                                    reason instanceof Error ? reason.message : String(reason)
                                  );
                                }
                              )
                            );
                          }}
                        >
                          {m.deployedAppRevertAction()}
                        </TargetTileOverflowMenuItem>
                      </TargetTileOverflowMenu>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
    }
  }

  return (
    <main className="studio-welcome">
      <div className="studio-welcome-backdrop" aria-hidden="true" />
      <div className="studio-welcome-shell">
        <nav className="studio-welcome-rail" aria-label={m.appsSectionLabel()}>
          <ul
            className="studio-welcome-rail-list"
            role="tablist"
            aria-orientation={railOrientation}
          >
            {navItems.map((item) => {
              const isActive = item.id === activeSection;
              const tabId = `studio-picker-tab-${item.id}`;
              const panelId = `studio-picker-panel-${item.id}`;
              return (
                <li key={item.id} role="presentation">
                  <button
                    id={tabId}
                    className={
                      isActive
                        ? `studio-welcome-rail-item ${item.className} is-active`
                        : `studio-welcome-rail-item ${item.className}`
                    }
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={panelId}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => {
                      selectSection(item.id, { userPicked: true });
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div
          className={
            activeSection === 'start'
              ? 'studio-welcome-content studio-welcome-content-start'
              : 'studio-welcome-content'
          }
        >
          {activeSection === 'start' ? undefined : (
            <header className="studio-welcome-hero">
              <div className="studio-welcome-logo-frame">
                <img className="studio-welcome-logo" src={logoUrl} alt={m.brandLogoAlt()} />
              </div>
              <p className="studio-welcome-eyebrow">{m.studioWelcomeEyebrow()}</p>
              <h1
                className={
                  titleVisible
                    ? 'studio-welcome-title'
                    : 'studio-welcome-title studio-welcome-title-fade'
                }
                aria-live="polite"
              >
                {welcomeTitle}
              </h1>
              <p className="studio-welcome-lead">{m.targetPickerLead()}</p>
            </header>
          )}
          {props.error !== undefined ? (
            <p className="alert" role="alert">
              {props.error}
            </p>
          ) : undefined}
          <div className="studio-picker-track">
            <div
              id={`studio-picker-panel-${activeSection}`}
              className="studio-welcome-panel studio-picker-page"
              role="tabpanel"
              aria-labelledby={`studio-picker-tab-${activeSection}`}
              data-picker-section={activeSection}
            >
              {renderSection(activeSection)}
            </div>
          </div>
        </div>
      </div>
      {deleteConfirmTarget !== undefined ? (
        <dialog
          ref={openAbandonConfirmDialog}
          className="studio-confirm-dialog"
          aria-labelledby="studio-delete-scratch-app-title"
          onCancel={(event) => {
            event.preventDefault();
            setDeleteConfirmTarget(undefined);
          }}
        >
          <h2 id="studio-delete-scratch-app-title">{m.deleteScratchAppConfirmTitle()}</h2>
          <p>
            {m.deleteScratchAppConfirmLead({
              label: deleteConfirmTarget.label,
            })}
          </p>
          <div className="studio-confirm-dialog-actions">
            <button
              type="button"
              className="button button-secondary"
              disabled={deletingScratchAppSlug !== undefined}
              onClick={() => {
                setDeleteConfirmTarget(undefined);
              }}
            >
              {m.deleteScratchAppCancel()}
            </button>
            <button
              type="button"
              className="button button-primary"
              disabled={deletingScratchAppSlug !== undefined}
              onClick={() => {
                const slug = parseScratchAppSlugFromTargetId(deleteConfirmTarget.id);
                setDeleteConfirmTarget(undefined);
                if (slug !== undefined) {
                  props.onDeleteScratchApp?.(slug);
                }
              }}
            >
              {m.deleteScratchAppConfirmAction()}
            </button>
          </div>
        </dialog>
      ) : undefined}
      {revertSlug !== undefined ? (
        <dialog className="studio-settings-dialog" open>
          <form
            method="dialog"
            className="studio-settings-form"
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <h2>{m.deployedAppRevertDialogTitle()}</h2>
            {revertBusy ? <p>{m.deployedAppRevertWorking()}</p> : undefined}
            {revertError !== undefined ? (
              <p className="alert" role="alert">
                {revertError}
              </p>
            ) : undefined}
            {!revertBusy && revertCommits.length === 0 && revertError === undefined ? (
              <p>{m.deployedAppRevertEmpty()}</p>
            ) : undefined}
            <ul className="studio-revert-commit-list">
              {revertCommits.map((commit) => {
                const deployment = deployments.find((item) => item.slug === revertSlug);
                return (
                  <li key={commit.sha}>
                    <button
                      className="button button-secondary studio-revert-commit"
                      type="button"
                      disabled={revertBusy || deployment === undefined}
                      onClick={() => {
                        if (deployment === undefined) {
                          return;
                        }
                        setRevertBusy(true);
                        setRevertError(undefined);
                        props.onRevertDeployment?.(deployment, commit.sha);
                        setRevertSlug(undefined);
                        setRevertCommits([]);
                        setRevertBusy(false);
                      }}
                    >
                      <span className="studio-revert-commit-sha">{commit.shortSha}</span>
                      <span className="studio-revert-commit-title">{commit.title}</span>
                      {isTruthyAndNotEmpty(commit.committedAt) ? (
                        <span className="studio-revert-commit-date">{commit.committedAt}</span>
                      ) : undefined}
                    </button>
                  </li>
                );
              })}
            </ul>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                setRevertSlug(undefined);
                setRevertCommits([]);
                setRevertError(undefined);
              }}
            >
              {m.deployedAppRevertCancel()}
            </button>
          </form>
        </dialog>
      ) : undefined}
    </main>
  );
}

export function HomePage(props: HomePageProps): React.ReactElement {
  const pendingAttachments = props.pendingAttachments ?? [];
  const selectedComponents = props.selectedComponents ?? [];
  const conversationRailOpen = props.conversationRailOpen !== false;
  const startingRequest = props.startingRequest === true;
  return (
    <div className="session-layout session-column">
      <div className="session-header">
        <div className="session-header-titles">
          <h1>{m.chatTitle()}</h1>
        </div>
        <ChatPanelToolbar
          mergeRequestUrl={undefined}
          agentBusy={false}
          showSessionActions={false}
          chatMaximized={props.chatMaximized === true}
          conversationRailOpen={conversationRailOpen}
          onOpenMergeRequest={() => undefined}
          onSyncSessionBase={() => undefined}
          onCopyLink={() => undefined}
          onAbandon={() => undefined}
          onToggleMaximize={props.onToggleMaximize ?? (() => undefined)}
          onToggleConversationRail={props.onToggleConversationRail ?? (() => undefined)}
        />
      </div>
      <div className="chat-log">
        <p className="studio-lead">{m.homeLead()}</p>
      </div>
      {props.error !== undefined ? (
        <p className="alert" role="alert">
          {props.error}
        </p>
      ) : undefined}
      {startingRequest ? (
        <div className="agent-trace starting-request" role="status" aria-live="polite">
          <span className="starting-request-spinner" aria-hidden="true" />
          <p className="agent-trace-title">{m.startingRequest()}</p>
        </div>
      ) : undefined}
      {sharedComposer({
        draft: props.requestDraft,
        agentBusy: false,
        locked: startingRequest,
        recallingHistory: false,
        pendingAttachments,
        selectedComponents,
        inspectEnabled: props.inspectEnabled === true,
        dictationSupported: props.dictationSupported !== false,
        dictationListening: props.dictationListening === true,
        screenshotSupported: props.screenshotSupported !== false,
        screenshotUsesSelection: props.screenshotUsesSelection === true,
        agentModelId: props.agentModelId,
        agentModels: props.agentModels,
        onAgentModelChange: props.onAgentModelChange,
        onDraftChange: props.onRequestDraftChange,
        onPickAttachments: props.onPickAttachments,
        onRemoveAttachment: props.onRemoveAttachment,
        onRemoveSelection: props.onRemoveSelection,
        onCaptureScreenshot: props.onCaptureScreenshot,
        onToggleInspect: props.onToggleInspect ?? (() => undefined),
        onToggleDictation: props.onToggleDictation ?? (() => undefined),
        onSend: props.onStartRequest,
        onStopTurn: () => undefined,
        onRecallOlder: () => undefined,
        onRecallNewer: () => undefined,
      })}
    </div>
  );
}

export function SessionPage(props: SessionPageProps): React.ReactElement {
  const { preview } = props.session;
  const progress = props.session.progress ?? [];
  const thinking = thinkingTrace(progress);
  const liveAssistant = lastAssistantText(progress);
  const pendingAttachments = props.pendingAttachments ?? [];
  const selectedComponents = props.selectedComponents ?? [];
  const conversationRailOpen = props.conversationRailOpen !== false;
  const chatLogRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const [showBrowserLivePreview, setShowBrowserLivePreview] = useState(() =>
    readShowBrowserLivePreview()
  );
  const browserLivePreviewUrl =
    props.session.agentBusy &&
    typeof props.session.browserPreviewUrl === 'string' &&
    props.session.browserPreviewUrl !== ''
      ? props.session.browserPreviewUrl
      : undefined;

  useEffect(() => {
    const node = chatLogRef.current;
    if (node === null) {
      return;
    }
    const nextTop = chatFollowScrollTop(node.scrollHeight, stickToBottomRef.current);
    if (nextTop === undefined) {
      return;
    }
    node.scrollTop = nextTop;
  }, [props.session.messages, liveAssistant, thinking, props.session.agentBusy]);

  return (
    <div className="session-layout session-column">
      <div className="session-header">
        <div className="session-header-titles">
          <ConversationTitleHeading
            title={props.session.title ?? ''}
            onRename={props.onRenameTitle}
          />
        </div>
        <ChatPanelToolbar
          mergeRequestUrl={props.session.mergeRequestUrl}
          agentBusy={props.session.agentBusy}
          showSessionActions
          favorited={props.session.favorited === true}
          canDeploy={props.canDeploy}
          isDeployed={props.isDeployed}
          deployBusy={props.deployBusy}
          currentScratchSlug={props.currentScratchSlug}
          deployedSlugs={props.deployedSlugs}
          chatMaximized={props.chatMaximized === true}
          conversationRailOpen={conversationRailOpen}
          onOpenMergeRequest={props.onOpenMergeRequest}
          onSyncSessionBase={props.onSyncSessionBase}
          onDeploySession={props.onDeploySession}
          onCopyLink={props.onCopyLink}
          onAbandon={props.onAbandon}
          onToggleFavorite={props.onToggleFavorite}
          onToggleMaximize={props.onToggleMaximize ?? (() => undefined)}
          onToggleConversationRail={props.onToggleConversationRail ?? (() => undefined)}
          sharing={{
            visibility: props.session.visibility,
            sharedWithEmails: props.session.sharedWithEmails,
            canManage: props.session.canManageSharing === true,
            busy: props.sharingBusy === true,
            onVisibilityChange: props.onVisibilityChange ?? (() => undefined),
            onSharedWithEmailsChange: props.onSharedWithEmailsChange ?? (() => undefined),
          }}
        />
      </div>
      {preview.status === 'off' ? (
        <p className="studio-status" role="status">
          {m.previewOff()}{' '}
          <button type="button" className="button button-primary" onClick={props.onStartPreview}>
            {m.startPreview()}
          </button>
        </p>
      ) : undefined}
      {props.notice !== undefined ? (
        <p className="studio-status" role="status">
          {props.notice}
        </p>
      ) : undefined}
      {props.session.mergeRequestUrl !== undefined ? (
        <p className="mr-link">
          {props.mergeRequestStatus === 'updated'
            ? m.mergeRequestUpdated()
            : m.mergeRequestOpened()}{' '}
          <a href={props.session.mergeRequestUrl}>{props.session.mergeRequestUrl}</a>
        </p>
      ) : undefined}
      {props.session.openspecArchiveMergeRequestUrl !== undefined ? (
        <p className="mr-link">
          {m.openspecArchiveMergeRequestOpened()}{' '}
          <a href={props.session.openspecArchiveMergeRequestUrl}>
            {props.session.openspecArchiveMergeRequestUrl}
          </a>
        </p>
      ) : undefined}
      {props.session.agentBusy ? (
        <div className="agent-trace" role="status" aria-live="polite">
          <p className="agent-trace-title">{m.agentWorking()}</p>
        </div>
      ) : undefined}
      {browserLivePreviewUrl !== undefined && showBrowserLivePreview ? (
        <figure className="browser-live-preview">
          <figcaption className="browser-live-preview-caption">
            <span>{m.browserLivePreview()}</span>
            <button
              type="button"
              className="browser-live-preview-toggle"
              onClick={() => {
                writeShowBrowserLivePreview(false);
                setShowBrowserLivePreview(false);
              }}
            >
              {m.browserLivePreviewHide()}
            </button>
          </figcaption>
          <img
            className="browser-live-preview-image"
            src={browserLivePreviewUrl}
            alt={m.browserLivePreviewAlt()}
          />
        </figure>
      ) : undefined}
      {browserLivePreviewUrl !== undefined && !showBrowserLivePreview ? (
        <div className="browser-live-preview-collapsed">
          <span>{m.browserLivePreview()}</span>
          <button
            type="button"
            className="browser-live-preview-toggle"
            onClick={() => {
              writeShowBrowserLivePreview(true);
              setShowBrowserLivePreview(true);
            }}
          >
            {m.browserLivePreviewShow()}
          </button>
        </div>
      ) : undefined}
      <div
        className="chat-log"
        ref={chatLogRef}
        onScroll={() => {
          const node = chatLogRef.current;
          if (node === null) {
            return;
          }
          stickToBottomRef.current = isChatStuckToBottom({
            scrollTop: node.scrollTop,
            clientHeight: node.clientHeight,
            scrollHeight: node.scrollHeight,
          });
        }}
      >
        {props.session.messages.map((message) => {
          const attachments = message.attachments ?? [];
          const messageTime = formatChatMessageTime(message.createdAt);
          return (
            <article key={message.id} className="chat-item">
              <div className="chat-item-meta">
                <strong>{chatSpeakerLabel(message)}</strong>
                {messageTime !== undefined ? (
                  <time
                    className="chat-item-time"
                    dateTime={new Date(message.createdAt ?? 0).toISOString()}
                  >
                    {messageTime}
                  </time>
                ) : undefined}
              </div>
              {message.text !== '' ? <ChatNotice text={message.text} /> : undefined}
              {attachments.length > 0 ? (
                <ul className="chat-attachments">
                  {attachments.map((attachment) => (
                    <li key={attachment.id}>
                      {attachment.kind === 'image' ? (
                        <a
                          className="chat-attachment-image-link"
                          href={attachment.url}
                          download={attachment.fileName}
                          target="_blank"
                          rel="noreferrer"
                          title={m.downloadAttachment({
                            fileName: attachment.fileName,
                          })}
                        >
                          <img
                            className="chat-attachment-image"
                            src={attachment.url}
                            alt={attachment.fileName}
                          />
                        </a>
                      ) : (
                        <a
                          className="chat-attachment-file"
                          href={attachment.url}
                          download={attachment.fileName}
                        >
                          {attachment.fileName}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              ) : undefined}
            </article>
          );
        })}
        {props.session.agentBusy
          ? thinking.map((step, index) => (
              <article
                key={`${step.kind}-${String(index)}`}
                className={`chat-item chat-item-trace agent-step-${step.kind}`}
              >
                <strong>{progressKindLabel(step.kind)}</strong>
                <pre>{step.text}</pre>
              </article>
            ))
          : undefined}
        {props.session.agentBusy && liveAssistant !== undefined ? (
          <article className="chat-item chat-item-live">
            <strong>{m.agentName()}</strong>
            <ChatMarkdown text={liveAssistant} />
          </article>
        ) : undefined}
      </div>
      {props.session.teach?.enabled === true &&
      (props.session.teach.status === 'recording' ||
        props.session.teach.status === 'ready' ||
        props.session.teach.status === 'finalizing') ? (
        <div className="teach-browser-bar" role="status">
          <span className="teach-browser-bar-label">
            {props.session.teach.status === 'ready'
              ? m.teachBrowserReady()
              : m.teachBrowserRecording()}
          </span>
          <div className="teach-browser-actions">
            {props.session.teach.status === 'recording' &&
            props.session.teach.viewerUrl !== undefined ? (
              <a
                className="button button-secondary"
                href={props.session.teach.viewerUrl}
                target="_blank"
                rel="noreferrer"
              >
                {m.teachBrowserOpenViewer()}
              </a>
            ) : undefined}
            {props.session.teach.status === 'ready' ? (
              <>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={props.teachBusy === true}
                  onClick={() => props.onTeachPromote?.()}
                >
                  {m.teachBrowserPromote()}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={props.teachBusy === true}
                  onClick={() => props.onTeachDiscard?.()}
                >
                  {m.teachBrowserDiscard()}
                </button>
              </>
            ) : undefined}
          </div>
        </div>
      ) : undefined}
      {props.session.sourceConnect?.enabled === true ? (
        <div className="teach-browser-bar" role="status">
          <span className="teach-browser-bar-label">{m.sourceConnectTitle()}</span>
          <div className="teach-browser-actions">
            {(['STIMCAR', 'IGOL', 'QAPTER'] as const).map((source) => {
              const state = props.session.sourceConnect?.sources?.[source];
              const busy =
                props.sourceConnectBusy === true ||
                props.session.agentBusy ||
                props.session.sourceConnect?.browserOwner === 'teach' ||
                props.session.sourceConnect?.browserOwner === 'agent';
              const connecting =
                props.session.sourceConnect?.activeSource === source &&
                props.session.sourceConnect.browserOwner === 'connect';
              return (
                <span key={source} className="source-connect-chip">
                  <button
                    type="button"
                    className="button button-secondary"
                    disabled={busy || connecting}
                    title={state?.message ?? source}
                    onClick={() => props.onSourceConnect?.(source)}
                  >
                    {connecting
                      ? m.sourceConnectConnecting()
                      : `${source} · ${m.sourceConnectAction()}`}
                  </button>
                  {connecting ? (
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={props.sourceConnectBusy === true}
                      onClick={() => props.onSourceConnectSkip?.(source)}
                    >
                      {m.sourceConnectSkip()}
                    </button>
                  ) : undefined}
                </span>
              );
            })}
            {props.session.sourceConnect.viewerUrl !== undefined ? (
              <a
                className="button button-secondary"
                href={props.session.sourceConnect.viewerUrl}
                target="_blank"
                rel="noreferrer"
              >
                {m.sourceConnectOpenViewer()}
              </a>
            ) : undefined}
          </div>
        </div>
      ) : undefined}
      {sharedComposer({
        draft: props.draft,
        agentBusy: props.session.agentBusy,
        recallingHistory: props.recallingHistory,
        pendingAttachments,
        selectedComponents,
        inspectEnabled: props.inspectEnabled === true,
        dictationSupported: props.dictationSupported !== false,
        dictationListening: props.dictationListening === true,
        screenshotSupported: props.screenshotSupported !== false,
        screenshotUsesSelection: props.screenshotUsesSelection === true,
        agentModelId: props.session.agentModelId,
        agentModels: props.session.agentModels,
        onAgentModelChange: props.onAgentModelChange,
        onDraftChange: props.onDraftChange,
        onPickAttachments: props.onPickAttachments,
        onRemoveAttachment: props.onRemoveAttachment,
        onRemoveSelection: props.onRemoveSelection,
        onCaptureScreenshot: props.onCaptureScreenshot,
        onToggleInspect: props.onToggleInspect ?? (() => undefined),
        onToggleDictation: props.onToggleDictation ?? (() => undefined),
        onSend: props.onSend,
        onStopTurn: props.onStopTurn,
        onRecallOlder: props.onRecallOlder,
        onRecallNewer: props.onRecallNewer,
        teachEnabled: props.session.teach?.enabled === true,
        teachStatus: props.session.teach?.status,
        teachBusy: props.teachBusy,
        onTeachToggle: () => {
          const status = props.session.teach?.status;
          if (status === 'recording' || status === 'finalizing') {
            props.onTeachStop?.();
            return;
          }
          props.onTeachStart?.();
        },
      })}
    </div>
  );
}

type SendSessionMessageInput = {
  readonly sessionId: string;
  readonly draft: string;
  readonly files: readonly DraftAttachmentFile[];
  readonly selections: readonly StudioInspectSelection[];
  readonly setSession: (value: SessionView) => void;
  readonly setDraft: (value: string) => void;
  readonly setDraftFiles: (value: readonly DraftAttachmentFile[]) => void;
  readonly setSelections: (value: readonly StudioInspectSelection[]) => void;
  readonly setError: SetError;
  readonly onUnauthorized: () => void;
};

async function fileToIncomingAttachment(file: File): Promise<IncomingAttachment> {
  const result = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = reader.result;
      if (typeof value !== 'string') {
        reject(new Error('read failed'));
        return;
      }
      resolve(value);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('read failed'));
    };
    reader.readAsDataURL(file);
  });
  const comma = result.indexOf(',');
  return {
    fileName: file.name,
    mimeType: canonicalAttachmentMimeType(file.type, file.name),
    data: comma === -1 ? result : result.slice(comma + 1),
  };
}

async function sendSessionMessage(input: SendSessionMessageInput): Promise<void> {
  const attachments = await Promise.all(
    input.files.map(async (item) => fileToIncomingAttachment(item.file))
  ).then(
    (value) => value,
    (reason: unknown) => {
      reportStudioError(reason, input.setError, input.onUnauthorized);
      return undefined;
    }
  );
  if (attachments === undefined) {
    return;
  }
  const next = await postSessionMessage(
    input.sessionId,
    mergeComposerPrompt(input.draft, input.selections),
    attachments
  ).then(
    (value) => value,
    (reason: unknown) => {
      reportStudioError(reason, input.setError, input.onUnauthorized);
      return undefined;
    }
  );
  if (next === undefined) {
    return;
  }
  input.setSession(next);
  input.setDraft('');
  revokeDraftAttachments(input.files);
  input.setDraftFiles([]);
  input.setSelections([]);
  input.setError(undefined);
}

async function startHomeRequest(
  input: SendSessionMessageInput & {
    readonly setPath: (path: string) => void;
    readonly setStartingRequest: (value: boolean) => void;
    readonly setSessionPreviewPath: (path: string | undefined) => void;
    readonly currentPreviewPath: string | undefined;
    readonly previewIframe: HTMLIFrameElement | null;
    readonly homePreviewUrl: string | undefined;
    readonly targetId?: string;
    readonly agentModelId?: string;
  }
): Promise<void> {
  if (!composerHasContent(input.draft, input.files.length, input.selections.length)) {
    input.setError(m.requestRequired());
    return;
  }
  input.setStartingRequest(true);
  try {
    const capturedPath =
      input.previewIframe !== null && isTruthyAndNotEmpty(input.homePreviewUrl)
        ? await requestPreviewLocation(input.previewIframe, input.homePreviewUrl).then(
            (path) => (path === undefined ? undefined : normalizePreviewPath(path)),
            () => undefined
          )
        : undefined;
    const created = await createSession({
      ...(input.targetId === undefined ? {} : { targetId: input.targetId }),
      ...(input.agentModelId === undefined ? {} : { agentModelId: input.agentModelId }),
    }).then(
      (value) => value,
      (reason: unknown) => {
        reportStudioError(reason, input.setError, input.onUnauthorized);
        return undefined;
      }
    );
    if (!isTruthy(created)) {
      return;
    }
    input.setSessionPreviewPath(capturedPath ?? input.currentPreviewPath);
    window.history.pushState({}, '', created.urlPath);
    input.setPath(created.urlPath);
    await sendSessionMessage({
      ...input,
      sessionId: created.id,
    });
  } finally {
    input.setStartingRequest(false);
  }
}

export function App(): React.ReactElement {
  const [loginMessage] = useState(consumeAuthenticationMessage);
  const [resource, setResource] = useState<AuthSessionResource>({
    state: 'loading',
  });
  useEffect(() => {
    const controller = new AbortController();
    runBackground(
      loadStudioSession(controller.signal).then(
        (session) => {
          if (session === null) {
            setResource({ state: 'anonymous' });
            return;
          }
          setResource({ state: 'authenticated', session });
        },
        (error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          setResource({ state: 'error' });
        }
      )
    );
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (resource.state !== 'authenticated') {
      return () => undefined;
    }
    const timer = window.setInterval(() => {
      runBackground(
        loadStudioSession().then(
          (session) => {
            if (session === null) {
              setResource({ state: 'anonymous' });
              return;
            }
            setResource({ state: 'authenticated', session });
          },
          (error: unknown) => {
            if (isUnauthorizedStudioError(error)) {
              setResource({ state: 'anonymous' });
            }
          }
        )
      );
    }, STUDIO_SESSION_KEEPALIVE_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [resource.state]);

  if (resource.state === 'loading') {
    return (
      <main className="studio-login">
        <p role="status">{m.loadingSession()}</p>
      </main>
    );
  }
  if (resource.state === 'error') {
    return (
      <main className="studio-login">
        <p className="alert" role="alert">
          {m.serviceTemporarilyUnavailable()}
        </p>
      </main>
    );
  }
  if (resource.state === 'anonymous') {
    return <AnonymousPage message={loginMessage} />;
  }
  return (
    <AuthenticatedStudio
      email={resource.session.email}
      givenName={resource.session.givenName}
      canCreateScratchApp={resource.session.capabilities.includes('create-studio-app')}
      capabilities={resource.session.capabilities}
      onUnauthorized={() => {
        setResource({ state: 'anonymous' });
      }}
    />
  );
}

export function AuthenticatedStudio(props: AuthenticatedStudioProps): React.ReactElement {
  const { email, givenName, canCreateScratchApp, capabilities, onUnauthorized } = props;
  const firstName = resolveWelcomeFirstName({ email, givenName });
  const [showCreateScratchAppMenu, setShowCreateScratchAppMenu] = useState(() =>
    readShowCreateScratchAppMenu()
  );
  const [showDeleteScratchAppMenu, setShowDeleteScratchAppMenu] = useState(() =>
    readShowDeleteScratchAppMenu()
  );
  const [showPreviewPathBar, setShowPreviewPathBar] = useState(() => readShowPreviewPathBar());
  const [showPromptActivityLog, setShowPromptActivityLog] = useState(() =>
    readShowPromptActivityLog()
  );
  const [preferredAgentModelId, setPreferredAgentModelId] = useState<string | undefined>(undefined);
  const [agentModels, setAgentModels] = useState<readonly AgentModelOptionView[]>([]);
  const canShowCreateScratchApp = canCreateScratchApp && showCreateScratchAppMenu;
  const canShowDeleteScratchApp = canCreateScratchApp && showDeleteScratchAppMenu;
  const [path, setPath] = useState(window.location.pathname);
  const sessionId = sessionIdFromPath(path);
  const onAppsMenu = isAppsPath(path);
  const [homePreviewUrl, setHomePreviewUrl] = useState<string | undefined>(undefined);
  const [sessionPreviewPath, setSessionPreviewPath] = useState<string | undefined>(undefined);
  const [pathBarDraft, setPathBarDraft] = useState('/');
  const [pathBarFocused, setPathBarFocused] = useState(false);
  const [pathBarError, setPathBarError] = useState<string | undefined>(undefined);
  const [pathHistory, setPathHistory] = useState<PreviewPathHistoryState>(() =>
    createPreviewPathHistory('/')
  );
  const [targets, setTargets] = useState<readonly StudioTargetSummaryView[]>([]);
  const [favorites, setFavorites] = useState<readonly FavoriteSessionSummaryView[]>([]);
  const [deployments, setDeployments] = useState<readonly DeployedAppSummaryView[]>([]);
  const [lastIndependentSession, setLastIndependentSession] = useState<SessionSummaryView | null>(
    null
  );
  const [deployBusy, setDeployBusy] = useState(false);

  function applySession(next: SessionView): void {
    setSession((previous) => mergeSessionFavoriteFlags(previous, next));
    if (isTruthyAndNotEmpty(next.agentModelId)) {
      setPreferredAgentModelId(next.agentModelId);
    }
    if (next.agentModels.length > 0) {
      setAgentModels(next.agentModels);
    }
  }
  const [selectedTargetId, setSelectedTargetId] = useState<string | undefined>(() =>
    readStoredSelectedTargetId()
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const [sessionAccessDenied, setSessionAccessDenied] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [session, setSession] = useState<SessionView | undefined>(undefined);
  const [draft, setDraft] = useState('');
  const [draftFiles, setDraftFiles] = useState<readonly DraftAttachmentFile[]>([]);
  const [selections, setSelections] = useState<readonly StudioInspectSelection[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | undefined>(undefined);
  const [conversationRailCollapsed, setConversationRailCollapsed] = useState(false);
  const [conversationRailShowAll, setConversationRailShowAll] = useState(false);
  const [conversationRailItems, setConversationRailItems] = useState<readonly SessionSummaryView[]>(
    []
  );
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [mergeRequestStatus, setMergeRequestStatus] = useState<'opened' | 'updated' | undefined>(
    undefined
  );
  const [previewReloadKey, setPreviewReloadKey] = useState(0);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [chatMaximized, setChatMaximized] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [inspectEnabled, setInspectEnabled] = useState(false);
  const [dictationListening, setDictationListening] = useState(false);
  const [startingRequest, setStartingRequest] = useState(false);
  const [hostBusySessionCount, setHostBusySessionCount] = useState(0);
  const [creatingScratchApp, setCreatingScratchApp] = useState(false);
  const [deletingScratchAppSlug, setDeletingScratchAppSlug] = useState<string | undefined>(
    undefined
  );
  const recognitionRef = useRef<ReturnType<typeof startSpeechDictation>>(undefined);
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const draftFilesRef = useRef(draftFiles);
  draftFilesRef.current = draftFiles;
  const dictationSupported = isSpeechDictationSupported();
  const screenshotSupported = isPreviewScreenshotSupported();
  const showTargetPicker =
    (onAppsMenu ||
      isRootPath(path) ||
      (sessionId === undefined && selectedTargetId === undefined)) &&
    !isAutomationsPath(path);

  useEffect(() => {
    return () => {
      revokeDraftAttachments(draftFilesRef.current);
    };
  }, []);

  useEffect(() => {
    setHistoryIndex(undefined);
  }, [sessionId]);

  useEffect(() => {
    const targetId = historyTargetIdForPath({
      sessionId,
      sessionTargetId: session?.targetId,
      selectedTargetId,
      pathTargetId: targetIdFromHostPreviewPath(path),
    });
    if (targetId === undefined) {
      setConversationRailItems([]);
      return () => undefined;
    }
    let cancelled = false;
    runBackground(
      listSessions(targetId).then(
        (items) => {
          if (!cancelled) {
            setConversationRailItems(items.filter((item) => item.targetId === targetId));
          }
        },
        (reason: unknown) => {
          if (!cancelled) {
            reportStudioError(reason, setError, onUnauthorized);
          }
        }
      )
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId, session?.targetId, session?.title, selectedTargetId, path, onUnauthorized]);

  useEffect(() => {
    if (notice === undefined) {
      return () => undefined;
    }
    const timer = window.setTimeout(() => {
      setNotice(undefined);
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [notice]);

  useEffect(() => {
    if (session?.mergeRequestUrl === undefined) {
      setMergeRequestStatus(undefined);
      return;
    }
    setMergeRequestStatus((current) => current ?? 'opened');
  }, [session?.mergeRequestUrl]);

  useEffect(() => {
    function onPopState(): void {
      setPath(window.location.pathname);
    }
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    runBackground(
      Promise.all([
        listTargets(),
        listFavorites(),
        listDeployments(),
        fetchLastIndependentSession(),
      ]).then(
        ([items, favoriteItems, deploymentItems, lastIndependent]) => {
          if (cancelled) {
            return;
          }
          setTargets(items);
          setFavorites(favoriteItems);
          setDeployments(deploymentItems);
          setLastIndependentSession(lastIndependent);
          setSelectedTargetId((current) =>
            resolveSelectedTargetId({
              current,
              stored: readStoredSelectedTargetId(),
              availableIds: items.map((item) => item.id),
            })
          );
        },
        (reason: unknown) => {
          if (!cancelled) {
            reportStudioError(reason, setError, onUnauthorized);
          }
        }
      )
    );
    return () => {
      cancelled = true;
    };
  }, [onUnauthorized, onAppsMenu]);

  useEffect(() => {
    let cancelled = false;

    function applyAgentStatus(status: Awaited<ReturnType<typeof fetchAgentStatus>>): void {
      setAgentModels(status.agentModels);
      setHostBusySessionCount(status.busySessionCount);
      setPreferredAgentModelId((current) =>
        current !== undefined && status.agentModels.some((model) => model.id === current)
          ? current
          : status.defaultAgentModelId
      );
    }

    function loadAgentStatus(): void {
      runBackground(
        fetchAgentStatus().then(
          (status) => {
            if (!cancelled) {
              applyAgentStatus(status);
            }
          },
          (reason: unknown) => {
            if (!cancelled) {
              reportStudioError(reason, setError, onUnauthorized);
            }
          }
        )
      );
    }

    loadAgentStatus();
    const pollMs = showTargetPicker ? 5_000 : undefined;
    const timer = pollMs === undefined ? undefined : window.setInterval(loadAgentStatus, pollMs);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearInterval(timer);
      }
    };
  }, [onUnauthorized, showTargetPicker]);

  useEffect(() => {
    if (!isRootPath(path)) {
      return;
    }
    navigateStudioPath(StudioPageRoutes.APPS, setPath, 'replace');
  }, [path]);

  useEffect(() => {
    if (!isLegacyHomePath(path)) {
      return;
    }
    navigateStudioPath(hostPreviewPathOrApps(selectedTargetId), setPath, 'replace');
  }, [path, selectedTargetId]);

  useEffect(() => {
    if (isKnownStudioPagePath(path)) {
      return;
    }
    navigateStudioPath(StudioPageRoutes.APPS, setPath, 'replace');
  }, [path]);

  useEffect(() => {
    const fromPath = targetIdFromHostPreviewPath(path);
    if (fromPath === undefined) {
      return;
    }
    const canonical = hostPreviewPathOrApps(fromPath);
    if (canonical !== path) {
      navigateStudioPath(canonical, setPath, 'replace');
    }
    if (selectedTargetId === fromPath) {
      return;
    }
    writeStoredSelectedTargetId(fromPath);
    setSelectedTargetId(fromPath);
  }, [path, selectedTargetId]);

  useEffect(() => {
    const fromPath = targetIdFromHostPreviewPath(path);
    if (fromPath === undefined || targets.length === 0) {
      return;
    }
    if (targets.some((target) => target.id === fromPath)) {
      return;
    }
    navigateStudioPath(StudioPageRoutes.APPS, setPath, 'replace');
  }, [path, targets]);

  useEffect(() => {
    if (sessionId === undefined) {
      setSessionPreviewPath(undefined);
      setPathBarDraft('/');
      setPathHistory(createPreviewPathHistory('/'));
      setPathBarError(undefined);
    } else {
      setPathHistory((current) => createPreviewPathHistory(previewPathHistoryCurrent(current)));
      setPathBarError(undefined);
    }
  }, [sessionId]);

  useEffect(() => {
    // Only reset path chrome on target switches while on home.
    // Session load may sync selectedTargetId without intending to drop handoff path.
    if (sessionId !== undefined) {
      return;
    }
    setSessionPreviewPath(undefined);
    setPathBarDraft('/');
    setPathHistory(createPreviewPathHistory('/'));
    setPathBarError(undefined);
  }, [selectedTargetId, sessionId]);

  useEffect(() => {
    if (pathBarFocused) {
      return;
    }
    setPathBarDraft(sessionPreviewPath ?? '/');
  }, [sessionPreviewPath, pathBarFocused]);

  useEffect(() => {
    let cancelled = false;
    let generation = 0;
    const pollHostPreview = shouldFetchHostPreviewStatus({
      sessionId,
      targetId: selectedTargetId,
      onAppsMenu,
    });
    function pullHostPreview(): void {
      if (selectedTargetId === undefined || !pollHostPreview) {
        return;
      }
      runBackground(
        fetchHostPreviewStatus(selectedTargetId).then(
          (status) => {
            if (cancelled) {
              return;
            }
            if (isTruthyAndNotEmpty(status.url)) {
              setHomePreviewUrl(status.url);
            }
            if (shouldReloadHostPreview(generation, status.generation)) {
              generation = status.generation;
              setPreviewReloadKey((key) => key + 1);
            }
          },
          (reason: unknown) => {
            if (!cancelled) {
              reportStudioError(reason, setError, onUnauthorized);
            }
          }
        )
      );
    }
    pullHostPreview();
    const timer = pollHostPreview
      ? window.setInterval(pullHostPreview, HOST_PREVIEW_STATUS_POLL_MS)
      : undefined;
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearInterval(timer);
      }
    };
  }, [sessionId, selectedTargetId, onAppsMenu, onUnauthorized]);

  useEffect(() => {
    setSessionAccessDenied(false);
    setSession(undefined);
    setError(undefined);
  }, [sessionId]);

  useEffect(() => {
    if (sessionId === undefined || sessionAccessDenied) {
      return () => undefined;
    }
    const id = sessionId;
    let cancelled = false;
    let starting = false;

    async function loadSession(): Promise<SessionView> {
      const next = await fetchSession(id);
      if (!cancelled) {
        applySession(next);
        writeStoredSelectedTargetId(next.targetId);
        setSelectedTargetId(next.targetId);
        setSessionAccessDenied(false);
      }
      return next;
    }

    function handleSessionLoadError(reason: unknown): void {
      if (cancelled) {
        return;
      }
      if (isSessionAccessForbidden(reason)) {
        setSessionAccessDenied(true);
        setSession(undefined);
        setError(m.sessionAccessDenied());
        return;
      }
      reportStudioError(reason, setError, onUnauthorized);
    }

    async function ensurePreview(): Promise<void> {
      if (!isPreviewTabActive(document.visibilityState) || starting) {
        return;
      }
      starting = true;
      const outcome = await loadSession()
        .then(async (current) => {
          if (cancelled || current.preview.status === 'live') {
            return;
          }
          const started = await startSessionPreview(id);
          if (!cancelled) {
            applySession(started);
          }
        })
        .then(
          () => undefined,
          (reason: unknown) => {
            handleSessionLoadError(reason);
          }
        );
      starting = false;
      return outcome;
    }

    function onVisibilityChange(): void {
      if (!isPreviewTabActive(document.visibilityState)) {
        return;
      }
      runBackground(ensurePreview());
      runBackground(touchSessionPreview(id));
    }

    runBackground(ensurePreview());
    document.addEventListener('visibilitychange', onVisibilityChange);
    const heartbeat = window.setInterval(() => {
      if (!isPreviewTabActive(document.visibilityState)) {
        return;
      }
      runBackground(ensurePreview());
      runBackground(touchSessionPreview(id));
    }, PREVIEW_HEARTBEAT_MS);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(heartbeat);
    };
  }, [sessionId, sessionAccessDenied, onUnauthorized]);

  useEffect(() => {
    if (session?.preview.status !== 'live') {
      return;
    }
    setPreviewReloadKey((key) => key + 1);
  }, [session?.preview.status]);

  const previousAgentBusyRef = useRef(false);
  useEffect(() => {
    previousAgentBusyRef.current = false;
  }, [sessionId]);
  useEffect(() => {
    const wasBusy = previousAgentBusyRef.current;
    const isBusy = session?.agentBusy === true;
    previousAgentBusyRef.current = isBusy;
    if (!shouldReloadPreviewAfterAgentTurn(wasBusy, isBusy, session?.preview.status === 'live')) {
      return;
    }
    setPreviewReloadKey((key) => key + 1);
  }, [session?.agentBusy, session?.preview.status]);

  useEffect(() => {
    if (sessionId === undefined || sessionAccessDenied) {
      return () => undefined;
    }
    const delayMs = sessionPollDelayMs(session?.agentBusy === true);
    const timer = window.setInterval(() => {
      runBackground(
        fetchSession(sessionId).then(applySession, (reason: unknown) => {
          if (isSessionAccessForbidden(reason)) {
            setSessionAccessDenied(true);
            setSession(undefined);
            setError(m.sessionAccessDenied());
            return;
          }
          reportStudioError(reason, setError, onUnauthorized);
        })
      );
    }, delayMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [sessionId, session?.agentBusy, sessionAccessDenied, onUnauthorized]);

  // Document listener: the composer textarea is disabled while agentBusy, so Escape
  // cannot be handled on the input itself (same cancel path as the Stop button).
  useEffect(() => {
    if (sessionId === undefined || session?.agentBusy !== true) {
      return () => undefined;
    }
    const activeSessionId = sessionId;
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (!shouldCancelTurnOnEscape(event)) {
        return;
      }
      event.preventDefault();
      runBackground(
        cancelSessionTurn(activeSessionId).then(applySession, (reason: unknown) => {
          reportStudioError(reason, setError, onUnauthorized);
        })
      );
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sessionId, session?.agentBusy, onUnauthorized]);

  const pendingAttachments = draftFiles.map(pendingAttachmentFromDraft);
  const siteUrl = resolveStudioPreviewUrl(session, homePreviewUrl, sessionPreviewPath);
  const previewOriginUrl =
    session?.preview.status === 'live' ? (session.preview.url ?? '') : (homePreviewUrl ?? '');
  const pathBarPresets = previewPathPresetsForTarget(selectedTargetId);

  function applyPreviewPathValue(rawInput: string): void {
    const parsed = parsePreviewPathInput({
      rawInput,
      previewOriginUrl,
    });
    if (!parsed.ok) {
      setPathBarError(
        parsed.reason === 'off-origin' ? m.previewPathOffOrigin() : m.previewPathInvalid()
      );
      return;
    }
    setPathBarError(undefined);
    setSessionPreviewPath(parsed.path);
    setPathBarDraft(parsed.path);
    setPathHistory((current) => pushPreviewPathHistory({ history: current, path: parsed.path }));
    setPreviewReloadKey((key) => key + 1);
  }

  function syncPreviewPathFromIframe(rawPath: string): void {
    if (pathBarFocused) {
      return;
    }
    const normalized = normalizePreviewPath(rawPath);
    const path = normalized === '' ? '/' : normalized;
    setPathBarError(undefined);
    setSessionPreviewPath(path);
    setPathBarDraft(path);
    setPathHistory((current) => pushPreviewPathHistory({ history: current, path }));
  }

  const composerHandlers = {
    onDraftChange: (value: string) => {
      setDraft(value);
      setHistoryIndex(undefined);
    },
    onPickAttachments: (files: readonly File[]) => {
      setDraftFiles((current) => [...current, ...files.map(createDraftAttachment)]);
    },
    onRemoveAttachment: (id: string) => {
      setDraftFiles((current) => {
        const removed = current.find((item) => item.id === id);
        if (removed !== undefined) {
          revokeDraftAttachment(removed);
        }
        return current.filter((item) => item.id !== id);
      });
    },
    onRemoveSelection: (index: number) => {
      setSelections((current) => current.filter((_selection, itemIndex) => itemIndex !== index));
    },
    onAgentModelChange: (modelId: string) => {
      setPreferredAgentModelId(modelId);
      if (session === undefined) {
        return;
      }
      runBackground(
        setSessionAgentModel(session.id, modelId).then(applySession, (reason: unknown) => {
          reportStudioError(reason, setError, onUnauthorized);
        })
      );
    },
    screenshotSupported:
      screenshotSupported &&
      !chatMaximized &&
      (session !== undefined
        ? session.preview.status === 'live'
        : isTruthyAndNotEmpty(homePreviewUrl)),
    screenshotUsesSelection: lastInspectSelection(selections) !== undefined,
    onCaptureScreenshot: () => {
      const iframe = previewIframeRef.current;
      if (iframe === null || chatMaximized) {
        setError(m.capturePreviewUnavailable());
        return;
      }
      const iframeRect = iframe.getBoundingClientRect();
      if (iframeRect.width < 1 || iframeRect.height < 1) {
        setError(m.capturePreviewUnavailable());
        return;
      }
      const selected = lastInspectSelection(selections);
      const wasInspecting = inspectEnabled;
      if (wasInspecting) {
        postToPreviewFrame(iframe, studioInspectRequest(false), siteUrl);
      }
      runBackground(
        capturePreviewScreenshot({
          iframe,
          previewUrl: siteUrl,
          mode: selected === undefined ? 'viewport' : 'selection',
          selector: selected?.selector,
          fileName: selected === undefined ? undefined : selectionScreenshotFileName(),
        })
          .then(
            (file) => {
              setDraftFiles((current) => [...current, createDraftAttachment(file)]);
              setError(undefined);
            },
            () => {
              setError(
                selected === undefined ? m.capturePreviewFailed() : m.captureSelectionUnavailable()
              );
            }
          )
          .then(
            () => {
              if (wasInspecting) {
                postToPreviewFrame(iframe, studioInspectRequest(true), siteUrl);
              }
            },
            () => {
              if (wasInspecting) {
                postToPreviewFrame(iframe, studioInspectRequest(true), siteUrl);
              }
            }
          )
      );
    },
    onToggleInspect: () => {
      if (chatMaximized) {
        setChatMaximized(false);
        setInspectEnabled(true);
      } else {
        setInspectEnabled(!inspectEnabled);
      }
      if (shouldReloadPreviewOnInspectToggle()) {
        setPreviewReloadKey((key) => key + 1);
      }
    },
    onToggleDictation: () => {
      toggleSpeechDictation({
        supported: dictationSupported,
        listening: dictationListening,
        recognitionRef,
        setListening: setDictationListening,
        setDraft: (value) => {
          setDraft(value);
          setHistoryIndex(undefined);
        },
      });
    },
    onToggleMaximize: () => {
      const next = toggleChatMaximized({ chatMaximized, chatCollapsed });
      setChatMaximized(next.chatMaximized);
      setChatCollapsed(next.chatCollapsed);
      setInspectEnabled(false);
    },
    onToggleConversationRail: () => {
      setConversationRailCollapsed((collapsed) => !collapsed);
    },
    onRecallOlder: () => {
      if (session === undefined) {
        return;
      }
      const step = stepUserMessageHistory({
        texts: userMessageTexts(session.messages),
        historyIndex,
        direction: 'older',
      });
      if (step === undefined) {
        return;
      }
      setHistoryIndex(step.historyIndex);
      setDraft(step.draft);
    },
    onRecallNewer: () => {
      if (session === undefined) {
        return;
      }
      const step = stepUserMessageHistory({
        texts: userMessageTexts(session.messages),
        historyIndex,
        direction: 'newer',
      });
      if (step === undefined) {
        return;
      }
      setHistoryIndex(step.historyIndex);
      setDraft(step.draft);
    },
  };

  const pathTargetId = targetIdFromHostPreviewPath(path);
  const conversationTargetId = historyTargetIdForPath({
    sessionId,
    sessionTargetId: session?.targetId,
    selectedTargetId,
    pathTargetId,
  });
  const conversationTargetLabel =
    session?.targetLabel ?? targets.find((target) => target.id === conversationTargetId)?.label;

  function refreshConversationRail(targetId: string): void {
    runBackground(
      listSessions(targetId).then(
        (items) => {
          setConversationRailItems(items.filter((item) => item.targetId === targetId));
        },
        (reason: unknown) => {
          reportStudioError(reason, setError, onUnauthorized);
        }
      )
    );
  }

  function selectConversationFromRail(urlPath: string): void {
    if (urlPath === window.location.pathname) {
      return;
    }
    window.history.pushState({}, '', urlPath);
    setPath(urlPath);
    setSession(undefined);
    setSessionPreviewPath(undefined);
    setDraft('');
    revokeDraftAttachments(draftFilesRef.current);
    setDraftFiles([]);
    setSelections([]);
    setHistoryIndex(undefined);
  }

  function startNewConversationFromRail(): void {
    if (conversationTargetId === undefined || creatingConversation) {
      return;
    }
    setCreatingConversation(true);
    runBackground(
      createSession({
        targetId: conversationTargetId,
        ...(preferredAgentModelId === undefined ? {} : { agentModelId: preferredAgentModelId }),
      }).then(
        (created) => {
          setCreatingConversation(false);
          window.history.pushState({}, '', created.urlPath);
          setPath(created.urlPath);
          applySession(created);
          setSessionPreviewPath(undefined);
          setDraft('');
          revokeDraftAttachments(draftFilesRef.current);
          setDraftFiles([]);
          setSelections([]);
          setHistoryIndex(undefined);
          refreshConversationRail(conversationTargetId);
        },
        (reason: unknown) => {
          setCreatingConversation(false);
          reportStudioError(reason, setError, onUnauthorized);
        }
      )
    );
  }

  const conversationRailNode =
    conversationTargetId === undefined ? undefined : (
      <ConversationRail
        items={conversationRailItems}
        currentSessionId={sessionId}
        viewerEmail={email}
        showAllConversations={conversationRailShowAll}
        collapsed={conversationRailCollapsed}
        targetLabel={conversationTargetLabel}
        creating={creatingConversation}
        onSelect={selectConversationFromRail}
        onNewConversation={startNewConversationFromRail}
        onToggleCollapse={() => {
          setConversationRailCollapsed((collapsed) => !collapsed);
        }}
        onToggleShowAllConversations={() => {
          setConversationRailShowAll((showAll) => !showAll);
        }}
      />
    );

  const shell = {
    previewUrl: siteUrl,
    previewReloadKey,
    previewReady: session === undefined || session.preview.status === 'live',
    error,
    chatMaximized,
    chatCollapsed,
    conversationRail: conversationRailNode,
    conversationRailCollapsed,
    inspectEnabled,
    previewIframeRef,
    pathBarDraft,
    pathBarVisible: showPreviewPathBar,
    pathBarError,
    pathBarCanBack: previewPathHistoryCanBack(pathHistory),
    pathBarCanForward: previewPathHistoryCanForward(pathHistory),
    pathBarPresets,
    previewDevice,
    onPathBarDraftChange: (value: string) => {
      setPathBarDraft(value);
      setPathBarError(undefined);
    },
    onPathBarFocusChange: setPathBarFocused,
    onPathBarApply: () => {
      applyPreviewPathValue(pathBarDraft);
    },
    onPathBarBack: () => {
      const next = previewPathHistoryBack(pathHistory);
      if (next === undefined) {
        return;
      }
      const path = previewPathHistoryCurrent(next);
      setPathHistory(next);
      setSessionPreviewPath(path);
      setPathBarDraft(path);
      setPathBarError(undefined);
      setPreviewReloadKey((key) => key + 1);
    },
    onPathBarForward: () => {
      const next = previewPathHistoryForward(pathHistory);
      if (next === undefined) {
        return;
      }
      const path = previewPathHistoryCurrent(next);
      setPathHistory(next);
      setSessionPreviewPath(path);
      setPathBarDraft(path);
      setPathBarError(undefined);
      setPreviewReloadKey((key) => key + 1);
    },
    onPathBarPreset: (path: string) => {
      applyPreviewPathValue(path);
    },
    onPreviewLocationPush: syncPreviewPathFromIframe,
    onInspectSelection: (selection: StudioInspectSelection) => {
      setSelections((current) => [...current, selection]);
    },
    onToggleCollapse: () => {
      const next = toggleChatCollapsed({ chatMaximized, chatCollapsed });
      setChatMaximized(next.chatMaximized);
      setChatCollapsed(next.chatCollapsed);
    },
    onPreviewFrameLoad: () => {
      if (sessionId === undefined) {
        return;
      }
      scheduleFavoriteThumbnailCapture({
        sessionId,
        iframe: previewIframeRef.current,
        previewUrl: siteUrl,
        captureRecommended: session?.thumbnailCaptureRecommended === true,
      });
    },
  };

  const openTargetMenu = (): void => {
    setSession(undefined);
    navigateStudioPath(StudioPageRoutes.APPS, setPath);
  };
  const studioFrameOptions: AuthenticatedFrameOptions = {
    onUnauthorized,
    onShowCreateScratchAppMenuChange: setShowCreateScratchAppMenu,
    onShowDeleteScratchAppMenuChange: setShowDeleteScratchAppMenu,
    onShowPreviewPathBarChange: setShowPreviewPathBar,
    onShowPromptActivityLogChange: setShowPromptActivityLog,
    showPromptActivityLog,
    ...(showTargetPicker ? {} : { onOpenTargetMenu: openTargetMenu }),
    ...(showTargetPicker || chatMaximized
      ? {}
      : {
          previewDevice,
          onPreviewDeviceChange: setPreviewDevice,
        }),
  };

  if (isAutomationsPath(path)) {
    return authenticatedFrame(
      email,
      <AutomationsPage capabilities={capabilities} onUnauthorized={onUnauthorized} />,
      studioFrameOptions
    );
  }

  if (showTargetPicker) {
    return authenticatedFrame(
      email,
      <TargetPickerPage
        firstName={firstName}
        targets={targets}
        favorites={favorites}
        deployments={deployments}
        lastIndependentSession={lastIndependentSession}
        error={error}
        creatingScratchApp={creatingScratchApp}
        canCreateScratchApp={canShowCreateScratchApp}
        canDeleteScratchApp={canShowDeleteScratchApp}
        deletingScratchAppSlug={deletingScratchAppSlug}
        quickStartDraft={draft}
        quickStartBusy={startingRequest}
        hostBusySessionCount={hostBusySessionCount}
        onQuickStartDraftChange={setDraft}
        onQuickStart={() => {
          if (startingRequest) {
            return;
          }
          const starterTargetId = resolveQuickStartTargetId(targets);
          if (starterTargetId === undefined) {
            setError(m.quickStartNoStarterTarget());
            return;
          }
          writeStoredSelectedTargetId(starterTargetId);
          setSelectedTargetId(starterTargetId);
          const starter = targets.find((target) => target.id === starterTargetId);
          if (starter !== undefined) {
            setHomePreviewUrl(starter.hostPreviewUrl);
          }
          runBackground(
            startHomeRequest({
              sessionId: '',
              draft,
              files: draftFiles,
              selections,
              setSession,
              setDraft,
              setDraftFiles,
              setSelections,
              setError,
              onUnauthorized,
              setPath,
              setStartingRequest,
              setSessionPreviewPath,
              currentPreviewPath: sessionPreviewPath,
              previewIframe: previewIframeRef.current,
              homePreviewUrl: starter?.hostPreviewUrl ?? homePreviewUrl,
              targetId: starterTargetId,
              agentModelId: preferredAgentModelId,
            })
          );
        }}
        onSelectFavorite={(urlPath) => {
          setConversationRailItems([]);
          setError(undefined);
          if (urlPath === window.location.pathname) {
            return;
          }
          window.history.pushState({}, '', urlPath);
          setPath(urlPath);
          setSession(undefined);
          setSessionPreviewPath(undefined);
          setDraft('');
          revokeDraftAttachments(draftFilesRef.current);
          setDraftFiles([]);
          setSelections([]);
          setHistoryIndex(undefined);
        }}
        onSelectLastIndependent={(urlPath) => {
          setConversationRailItems([]);
          setError(undefined);
          if (urlPath === window.location.pathname) {
            return;
          }
          window.history.pushState({}, '', urlPath);
          setPath(urlPath);
          setSession(undefined);
          setSessionPreviewPath(undefined);
          setDraft('');
          revokeDraftAttachments(draftFilesRef.current);
          setDraftFiles([]);
          setSelections([]);
          setHistoryIndex(undefined);
        }}
        onSelectDeployment={(deployment) => {
          window.open(deployment.url, '_blank', 'noopener,noreferrer');
        }}
        onEditDeployment={(deployment) => {
          writeStoredSelectedTargetId(deployment.targetId);
          setSelectedTargetId(deployment.targetId);
          setConversationRailItems([]);
          setError(undefined);
          setSession(undefined);
          const hostPath = hostPreviewPathOrApps(deployment.targetId);
          window.history.pushState({}, '', hostPath);
          setPath(hostPath);
        }}
        onStopDeployment={(deployment) => {
          setError(undefined);
          runBackground(
            stopDeployment(deployment.slug).then(
              () => {
                setDeployments((current) =>
                  current.filter((item) => item.slug !== deployment.slug)
                );
              },
              (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              }
            )
          );
        }}
        onRevertDeployment={(deployment, sha) => {
          setError(undefined);
          runBackground(
            revertDeployment(deployment.slug, sha).then(
              (updated) => {
                setDeployments((current) =>
                  current.map((item) => (item.slug === updated.slug ? updated : item))
                );
                setNotice(m.deploySessionSucceeded({ hostname: updated.hostname }));
              },
              (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              }
            )
          );
        }}
        onSelectTarget={(targetId) => {
          const selected = targets.find((target) => target.id === targetId);
          writeStoredSelectedTargetId(targetId);
          setSelectedTargetId(targetId);
          setConversationRailItems([]);
          setError(undefined);
          if (isTruthyAndNotEmpty(selected?.resumeSessionId)) {
            const sessionPath = StudioPageRoutes.SESSION(selected.resumeSessionId);
            setSession(undefined);
            window.history.pushState({}, '', sessionPath);
            setPath(sessionPath);
            return;
          }
          setSession(undefined);
          if (selected !== undefined) {
            setHomePreviewUrl(selected.hostPreviewUrl);
            setPreviewReloadKey((key) => key + 1);
          }
          navigateStudioPath(hostPreviewPathOrApps(targetId), setPath);
        }}
        onCreateScratchApp={(slug) => {
          if (creatingScratchApp) {
            return;
          }
          setCreatingScratchApp(true);
          setError(undefined);
          runBackground(
            createScratchApp({ slug }).then(
              (created) => {
                setCreatingScratchApp(false);
                writeStoredSelectedTargetId(created.id);
                setSelectedTargetId(created.id);
                setConversationRailItems([]);
                setSession(undefined);
                setHomePreviewUrl(created.hostPreviewUrl);
                setPreviewReloadKey((key) => key + 1);
                setTargets((current) => {
                  const without = current.filter((target) => target.id !== created.id);
                  return [...without, created];
                });
                navigateStudioPath(hostPreviewPathOrApps(created.id), setPath);
              },
              (reason: unknown) => {
                setCreatingScratchApp(false);
                reportStudioError(reason, setError, onUnauthorized);
              }
            )
          );
        }}
        onDeleteScratchApp={(slug) => {
          if (deletingScratchAppSlug !== undefined) {
            return;
          }
          setDeletingScratchAppSlug(slug);
          setError(undefined);
          runBackground(
            deleteScratchApp(slug).then(
              () => {
                setDeletingScratchAppSlug(undefined);
                setTargets((current) =>
                  current.filter((target) => parseScratchAppSlugFromTargetId(target.id) !== slug)
                );
                setLastIndependentSession((current) =>
                  current !== null && parseScratchAppSlugFromTargetId(current.targetId) === slug
                    ? null
                    : current
                );
                setSelectedTargetId((current) => {
                  if (current !== undefined && parseScratchAppSlugFromTargetId(current) === slug) {
                    return undefined;
                  }
                  return current;
                });
              },
              (reason: unknown) => {
                setDeletingScratchAppSlug(undefined);
                reportStudioError(reason, setError, onUnauthorized);
              }
            )
          );
        }}
      />,
      studioFrameOptions
    );
  }

  if (sessionId !== undefined) {
    if (sessionAccessDenied) {
      return authenticatedFrame(
        email,
        <StudioShell {...shell}>
          <h1>{m.sessionAccessDenied()}</h1>
          <p className="alert" role="alert">
            {error ?? m.sessionAccessDeniedLead()}
          </p>
          <p>{m.sessionAccessDeniedLead()}</p>
        </StudioShell>,
        studioFrameOptions
      );
    }
    if (session === undefined) {
      return authenticatedFrame(
        email,
        <StudioShell {...shell}>
          <h1>{m.chatTitle()}</h1>
          {error !== undefined ? (
            <p className="alert" role="alert">
              {error}
            </p>
          ) : (
            <p>{m.sessionLoading()}</p>
          )}
        </StudioShell>,
        studioFrameOptions
      );
    }
    return authenticatedFrame(
      email,
      <StudioShell {...shell}>
        <SessionPage
          session={session}
          draft={draft}
          recallingHistory={historyIndex !== undefined}
          pendingAttachments={pendingAttachments}
          selectedComponents={selections}
          inspectEnabled={inspectEnabled}
          dictationSupported={dictationSupported}
          dictationListening={dictationListening}
          chatMaximized={chatMaximized}
          conversationRailOpen={!conversationRailCollapsed}
          notice={notice}
          onRenameTitle={(title) => {
            runBackground(
              renameSessionTitle(session.id, title).then(
                (next) => {
                  applySession(next);
                  if (conversationTargetId !== undefined) {
                    refreshConversationRail(conversationTargetId);
                  }
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          mergeRequestStatus={mergeRequestStatus}
          onSend={() => {
            setHistoryIndex(undefined);
            runBackground(
              sendSessionMessage({
                sessionId: session.id,
                draft,
                files: draftFiles,
                selections,
                setSession: applySession,
                setDraft,
                setDraftFiles,
                setSelections,
                setError,
                onUnauthorized,
              })
            );
          }}
          onStartPreview={() => {
            runBackground(
              startSessionPreview(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onOpenMergeRequest={() => {
            const hadMergeRequest = session.mergeRequestUrl !== undefined;
            runBackground(
              openSessionMergeRequest(session.id).then(
                (next) => {
                  applySession(next);
                  setMergeRequestStatus(hadMergeRequest ? 'updated' : 'opened');
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onSyncSessionBase={() => {
            runBackground(
              syncSessionBase(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          canDeploy={session.targetId.startsWith('scratch-app:')}
          isDeployed={deployments.some((item) => item.targetId === session.targetId)}
          deployBusy={deployBusy}
          currentScratchSlug={scratchSlugFromTargetId(session.targetId)}
          deployedSlugs={deployments.map((item) => item.slug)}
          onDeploySession={(request) => {
            setDeployBusy(true);
            setError(undefined);
            runBackground(
              deploySession(session.id, request).then(
                (deployment) => {
                  setDeployBusy(false);
                  setDeployments((current) => {
                    const without = current.filter((item) => item.slug !== deployment.slug);
                    return [deployment, ...without];
                  });
                  setNotice(m.deploySessionSucceeded({ hostname: deployment.hostname }));
                },
                (reason: unknown) => {
                  setDeployBusy(false);
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onAbandon={() => {
            runBackground(
              abandonSession(session.id).then(
                () => {
                  window.location.assign(hostPreviewPathOrApps(session.targetId));
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onCopyLink={() => {
            runBackground(
              navigator.clipboard.writeText(window.location.href).then(
                () => {
                  setNotice(m.linkCopied());
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onToggleFavorite={() => {
            const currentlyFavorited = session.favorited === true;
            runBackground(
              (currentlyFavorited ? unpinFavorite(session.id) : pinFavorite(session.id)).then(
                (next) => {
                  applySession(next);
                  if (!currentlyFavorited) {
                    scheduleFavoriteThumbnailCapture({
                      sessionId: session.id,
                      iframe: previewIframeRef.current,
                      previewUrl: siteUrl,
                      captureRecommended: true,
                      debounceMs: 0,
                    });
                  }
                  runBackground(listFavorites().then(setFavorites, () => undefined));
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          sharingBusy={sharingBusy}
          onVisibilityChange={(visibility) => {
            setSharingBusy(true);
            runBackground(
              updateSessionSharing(session.id, { visibility }).then(
                (next) => {
                  setSharingBusy(false);
                  applySession(next);
                },
                (reason: unknown) => {
                  setSharingBusy(false);
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onSharedWithEmailsChange={(sharedWithEmails) => {
            setSharingBusy(true);
            runBackground(
              updateSessionSharing(session.id, { sharedWithEmails }).then(
                (next) => {
                  setSharingBusy(false);
                  applySession(next);
                },
                (reason: unknown) => {
                  setSharingBusy(false);
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onStopTurn={() => {
            runBackground(
              cancelSessionTurn(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onTeachStart={() => {
            runBackground(
              startTeachBrowser(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onTeachStop={() => {
            runBackground(
              stopTeachBrowser(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onTeachPromote={() => {
            runBackground(
              promoteTeachBrowser(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onTeachDiscard={() => {
            runBackground(
              discardTeachBrowser(session.id).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          onSourceConnect={(source) => {
            runBackground(
              startSourceConnect(session.id, source).then(
                (next) => {
                  applySession(next);
                  if (next.sourceConnect?.viewerUrl !== undefined) {
                    window.open(next.sourceConnect.viewerUrl, '_blank', 'noopener,noreferrer');
                  }
                },
                (reason: unknown) => {
                  reportStudioError(reason, setError, onUnauthorized);
                }
              )
            );
          }}
          onSourceConnectSkip={(source) => {
            runBackground(
              skipSourceConnect(session.id, source).then(applySession, (reason: unknown) => {
                reportStudioError(reason, setError, onUnauthorized);
              })
            );
          }}
          {...composerHandlers}
        />
      </StudioShell>,
      studioFrameOptions
    );
  }

  return authenticatedFrame(
    email,
    <StudioShell {...shell}>
      <HomePage
        requestDraft={draft}
        error={error}
        startingRequest={startingRequest}
        pendingAttachments={pendingAttachments}
        selectedComponents={selections}
        inspectEnabled={inspectEnabled}
        dictationSupported={dictationSupported}
        dictationListening={dictationListening}
        chatMaximized={chatMaximized}
        conversationRailOpen={!conversationRailCollapsed}
        onRequestDraftChange={setDraft}
        onStartRequest={() => {
          if (startingRequest) {
            return;
          }
          runBackground(
            startHomeRequest({
              sessionId: '',
              draft,
              files: draftFiles,
              selections,
              setSession,
              setDraft,
              setDraftFiles,
              setSelections,
              setError,
              onUnauthorized,
              setPath,
              setStartingRequest,
              setSessionPreviewPath,
              currentPreviewPath: sessionPreviewPath,
              previewIframe: previewIframeRef.current,
              homePreviewUrl,
              targetId: selectedTargetId,
              agentModelId: preferredAgentModelId,
            })
          );
        }}
        agentModelId={preferredAgentModelId}
        agentModels={agentModels}
        {...composerHandlers}
      />
    </StudioShell>,
    studioFrameOptions
  );
}

type DictationToggleInput = {
  readonly supported: boolean;
  readonly listening: boolean;
  readonly recognitionRef: MutableRefObject<ReturnType<typeof startSpeechDictation>>;
  readonly setListening: (value: boolean) => void;
  readonly setDraft: Dispatch<SetStateAction<string>>;
};

function toggleSpeechDictation(input: DictationToggleInput): void {
  const { supported, listening, recognitionRef, setListening, setDraft } = input;
  if (!supported) {
    return;
  }
  if (listening) {
    stopSpeechDictation(recognitionRef.current);
    setListening(false);
    return;
  }
  const recognition = startSpeechDictation({
    onTranscript: (text) => {
      setDraft((value) => appendDictationTranscript(value, text));
    },
    onStop: () => {
      setListening(false);
    },
  });
  if (recognition === undefined) {
    return;
  }
  recognitionRef.current = recognition;
  setListening(true);
}
